import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Tabs,
  TextContainer,
  Text,
  Button,
  LegacyStack,
  FormLayout,
  TextField,
  Select,
  Banner,
  Badge,
  Spinner,
  Modal,
  ChoiceList,
  Icon,
  Tooltip,
  EmptyState,
  Checkbox,
  Divider
} from '@shopify/polaris';
import { 
  PlusMinor, 
  DeleteMinor, 
  EditMinor, 
  DuplicateMinor,
  ViewMinor,
  ImageMajor,
  TextMajor,
  ColorsMajor,
  ExportMinor,
  ArrowLeftMinor,
  ArrowRightMinor,
  ArrowUpMinor,
  ArrowDownMinor
} from '@shopify/polaris-icons';
import MeasurementDiagram from '../../components/sizecharts/MeasurementDiagram';
import { useShop } from '../../contexts/ShopContext';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { 
  doc, 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  serverTimestamp 
} from 'firebase/firestore';
import { hasEnoughCreditsForAIModel } from '../../services/creditService';
import { deductCredits } from '../../services/optimizedCreditService';
import html2canvas from 'html2canvas';
// Import Google Fonts API helper if available, or we'll implement a simple version
import { Popover, ColorPicker } from '@shopify/polaris';

// Import custom ColorPickerField component
import ColorPickerField from '../../components/common/ColorPickerField';

// Import custom components for garment template
import FitGuideVisualizer from '../../components/sizecharts/FitGuideVisualizer';
import { analyzeStretchLevel, garmentTypes, measurementPoints } from '../../services/garmentTemplateService';
import { defaultTranslations, translateMeasurementPoints, translateGarmentTypes, getAvailableLanguages } from '../../services/translationsService';

// Shop context is already imported above

// Import size chart export components and services
import SizeChartExportOptions from '../../components/sizecharts/SizeChartExportOptions';
import { exportChartAsImage as exportChartService, uploadChartToShopify as uploadChartService } from '../../services/sizeChartExportService';

export default function SizeChartDesignerNew({
  shopId,
  userId,
  settings,
  refreshData,
  showTemplateDesignOnly = false,
  showGeneratedChartsOnly = false,
  accessToken = null
}) {
  // Get shop context
  const { currentShop } = useShop();
  
  // State for tab selection
  const [selected, setSelected] = useState(0);
  
  // Ensure all color values are valid
  useEffect(() => {
    // Create a copy of the chartStyle object
    const validatedChartStyle = {...chartStyle};
    let hasChanges = false;
    
    // Check all color properties and ensure they have valid values
    Object.keys(chartStyle).forEach(key => {
      if (key.toLowerCase().includes('color')) {
        const validColor = ensureValidHexColor(chartStyle[key]);
        if (validColor !== chartStyle[key]) {
          validatedChartStyle[key] = validColor;
          hasChanges = true;
        }
      }
    });
    
    // Only update state if changes were made
    if (hasChanges) {
      setChartStyle(validatedChartStyle);
    }
  }, []);

  // State for template design
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [headerText, setHeaderText] = useState('');
  const [footerText, setFooterText] = useState('');
  const [infoText, setInfoText] = useState('');
  const [showHeaderImage, setShowHeaderImage] = useState(false);
  const [headerImageUrl, setHeaderImageUrl] = useState('');
  const [showFooterImage, setShowFooterImage] = useState(false);
  const [footerImageUrl, setFooterImageUrl] = useState('');
  const [chartStyle, setChartStyle] = useState({
    // Global styles
    fontFamily: 'sans-serif',
    chartTitleColor: '#212b36',  // Added chart title color
    
    // Size Chart Table styles (first in preview)
    borderColor: '#dfe3e8',
    headerBgColor: '#f4f6f8',
    headerTextColor: '#212b36',
    cellTextColor: '#212b36',
    alternateRowTextColor: '#212b36',
    rowAlternateColor: '#f9fafb',
    
    // How to Measure section styles (second in preview)
    measurementBgColor: '#f4f6f8',
    measurementHeadingColor: '#212b36',
    measurementTextColor: '#637381',
    measurementNumberBgColor: '#0068d9',
    measurementNumberColor: '#ffffff',
    
    // Fit Guide specific styles (third in preview)
    fitGuideHeadingColor: '#212b36',
    fitGuideTextColor: '#637381',
    fitGuideAccentColor: '#0068d9',
    fitGuideCardBgColor: '#ffffff',
    fitGuideTagBgColor: '#f2f7fe',
    fitGuideTagTextColor: '#0068d9',
    
    // Fit Guide Visualizer styles (fourth in preview)
    fitGuideBgColor: '#f4f6f8',
    fitGuideDotColor: '#000000', // Color of the indicator dots
    fitGuideTrackColor: '#e6e6e6', // Color of the track/bar
    fitGuideLabelColor: '#637381', // Color of the labels (Slim, Regular, Loose, etc.)
    fitGuideIndicatorShape: 'dot', // Shape of the indicator: 'dot', 'rectangle', or 'triangle'
    fitGuideIndicatorSize: 16, // Size of the indicator in pixels
    fitGuideIndicatorOffset: 0, // Vertical offset of the indicator in pixels (positive = down, negative = up)
    
    // Notes section styles (fifth in preview)
    notesBgColor: '#f9fafb',
    notesHeadingColor: '#212b36',
    notesTextColor: '#637381',
    
    // Footer and info styles (sixth in preview)
    footerTextColor: '#637381',
    infoTextColor: '#637381',
    infoBgColor: '#f9fafb'
  });
  
  // State for font family picker
  const [fontFamilyPickerActive, setFontFamilyPickerActive] = useState(false);
  
  // Garment template state
  const [garmentType, setGarmentType] = useState('dress');
  const [fitType, setFitType] = useState('regular');
  const [stretchLevel, setStretchLevel] = useState('none');
  const [materialInfo, setMaterialInfo] = useState('');
  const [showMeasurementDiagram, setShowMeasurementDiagram] = useState(true);
  
  // Translation state
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [translatedMeasurementPoints, setTranslatedMeasurementPoints] = useState(measurementPoints);
  const [translatedGarmentTypes, setTranslatedGarmentTypes] = useState(garmentTypes);
  const [languageOptions, setLanguageOptions] = useState(defaultTranslations.languages);
  
  // Load language settings from Firebase
  const loadLanguageSettings = async () => {
    try {
      const languageSettingsRef = doc(db, `shops/${shopId}/settings/language`);
      const docSnap = await getDoc(languageSettingsRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        
        // Set default language if available
        if (data.defaultLanguage) {
          setSelectedLanguage(data.defaultLanguage);
        }
        
        // Combine default languages with custom languages
        if (data.customLanguages && Array.isArray(data.customLanguages)) {
          const allLanguages = [...defaultTranslations.languages, ...data.customLanguages];
          setLanguageOptions(allLanguages);
        }
      }
    } catch (error) {
      console.error('Error loading language settings:', error);
    }
  };
  
  // Load language settings when component mounts
  useEffect(() => {
    if (shopId) {
      loadLanguageSettings();
    }
  }, [shopId]);
  
  // Update translations when language changes
  useEffect(() => {
    if (selectedLanguage) {
      setTranslatedMeasurementPoints(translateMeasurementPoints(measurementPoints, selectedLanguage));
      setTranslatedGarmentTypes(translateGarmentTypes(garmentTypes, selectedLanguage));
    }
  }, [selectedLanguage]);
  
  // Ensure a valid hex color (with # prefix)
  const ensureValidHexColor = (color) => {
    // Default color if no valid color is provided
    const defaultColor = '#212b36';
    
    try {
      // If color is not a string or is empty, return default color
      if (typeof color !== 'string' || color.trim() === '') {
        return defaultColor;
      }
      
      // Add # if missing
      if (color.charAt(0) !== '#') {
        color = '#' + color;
      }
      
      // Validate hex format (#RRGGBB or #RGB)
      const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
      if (!hexRegex.test(color)) {
        // If invalid format, try to fix it
        // Remove any non-hex characters
        const cleanedColor = '#' + color.replace(/[^0-9A-Fa-f]/g, '').substring(0, 6);
        
        // If cleaned color is still not valid, return default
        if (!hexRegex.test(cleanedColor) || cleanedColor === '#') {
          return defaultColor;
        }
        
        // If cleaned color is too short, pad it
        if (cleanedColor.length < 7) {
          const padding = '0'.repeat(7 - cleanedColor.length);
          return cleanedColor + padding;
        }
        
        return cleanedColor;
      }
      
      // Convert 3-digit hex to 6-digit hex for consistency
      if (color.length === 4) {
        const r = color.charAt(1);
        const g = color.charAt(2);
        const b = color.charAt(3);
        color = `#${r}${r}${g}${g}${b}${b}`;
      }
      
      return color;
    } catch (error) {
      // If any error occurs, return the default color
      console.error('Error in ensureValidHexColor:', error);
      return defaultColor;
    }
  };
  
  // Handle color selection from the color picker
  const handleColorChange = (color, property) => {
    try {
      // Check if we have a valid color object
      if (color && typeof color === 'object') {
        // If it has an hex property, use that
        if (color.hex) {
          setChartStyle({...chartStyle, [property]: `#${color.hex}`});
        }
        // If it has RGB components
        else if (color.red !== undefined && color.green !== undefined && color.blue !== undefined) {
          const r = color.red.toString(16).padStart(2, '0');
          const g = color.green.toString(16).padStart(2, '0');
          const b = color.blue.toString(16).padStart(2, '0');
          setChartStyle({...chartStyle, [property]: `#${r}${g}${b}`});
        }
      }
    } catch (error) {
      console.error('Error handling color change:', error);
      // Fallback to default colors if something goes wrong
      const fallbacks = {
        borderColor: '#dfe3e8',
        headerBgColor: '#f4f6f8',
        rowAlternateColor: '#f9fafb'
      };
      setChartStyle({...chartStyle, [property]: fallbacks[property] || fallbacks.borderColor});
    }
  };
  
  // Comprehensive Google Fonts list
  const [googleFonts, setGoogleFonts] = useState([
    { label: 'Sans-serif', value: 'sans-serif' },
    { label: 'Serif', value: 'serif' },
    { label: 'Monospace', value: 'monospace' },
    // Popular Google Fonts - this is just the initial list, we'll load more
    { label: 'Roboto', value: '"Roboto", sans-serif' },
    { label: 'Open Sans', value: '"Open Sans", sans-serif' },
    { label: 'Lato', value: '"Lato", sans-serif' },
    { label: 'Montserrat', value: '"Montserrat", sans-serif' },
    { label: 'Raleway', value: '"Raleway", sans-serif' },
    { label: 'Poppins', value: '"Poppins", sans-serif' }
  ]);
  
  // Font search state
  const [fontSearchQuery, setFontSearchQuery] = useState('');
  const [filteredFonts, setFilteredFonts] = useState([]);
  const [isLoadingFonts, setIsLoadingFonts] = useState(false);
  const [allGoogleFonts, setAllGoogleFonts] = useState([]);
  
  // Load all Google Fonts
  useEffect(() => {
    const fetchGoogleFonts = async () => {
      try {
        setIsLoadingFonts(true);
        // Fetch from Google Fonts API
        const response = await fetch('https://www.googleapis.com/webfonts/v1/webfonts?key=AIzaSyAOES8EmKhuJEnsn9kS1XKBpxxp-TgN8Jc&sort=popularity');
        const data = await response.json();
        
        if (data && data.items) {
          // Format the fonts for the dropdown
          const formattedFonts = data.items.map(font => ({
            label: font.family,
            value: `"${font.family}", ${font.category}`,
            category: font.category
          }));
          
          // Add the basic system fonts at the top
          const systemFonts = [
            { label: 'Sans-serif', value: 'sans-serif', category: 'sans-serif' },
            { label: 'Serif', value: 'serif', category: 'serif' },
            { label: 'Monospace', value: 'monospace', category: 'monospace' }
          ];
          
          setAllGoogleFonts([...systemFonts, ...formattedFonts]);
          setIsLoadingFonts(false);
        }
      } catch (error) {
        console.error('Error fetching Google Fonts:', error);
        setIsLoadingFonts(false);
      }
    };
    
    fetchGoogleFonts();
  }, []);
  
  // Load Google Fonts
  useEffect(() => {
    // Add Google Fonts link to head
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&family=Open+Sans:wght@400;700&family=Lato:wght@400;700&family=Montserrat:wght@400;700&family=Raleway:wght@400;700&family=Poppins:wght@400;700&family=Playfair+Display:wght@400;700&family=Merriweather:wght@400;700&family=Ubuntu:wght@400;700&display=swap';
    document.head.appendChild(link);
    
    return () => {
      // Clean up when component unmounts
      document.head.removeChild(link);
    };
  }, []);
  
  // State for generated charts
  const [generatedCharts, setGeneratedCharts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Modal states
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isChartPreviewModalOpen, setIsChartPreviewModalOpen] = useState(false);
  const [previewChart, setPreviewChart] = useState(null);
  
  // Export options state
  const [isExportOptionsModalOpen, setIsExportOptionsModalOpen] = useState(false);
  const [chartToExport, setChartToExport] = useState(null);
  const [exportOperation, setExportOperation] = useState('export'); // 'export' or 'upload'
  
  // Handle tab change
  const handleTabChange = useCallback((selectedTabIndex) => {
    setSelected(selectedTabIndex);
  }, []);
  
  // Define tabs
  const tabs = [
    {
      id: 'generated-charts',
      content: 'Generated Charts',
      accessibilityLabel: 'Generated Charts',
      panelID: 'generated-charts-panel',
    },
    {
      id: 'template-design',
      content: 'Template Design',
      accessibilityLabel: 'Template Design',
      panelID: 'template-design-panel',
    }
  ];
  
  // Load templates and generated charts on component mount
  useEffect(() => {
    if (shopId) {
      loadTemplates();
      loadGeneratedCharts();
    }
  }, [shopId]);
  
  // Load templates from Firestore
  const loadTemplates = async () => {
    try {
      setIsLoading(true);
      const templatesRef = collection(db, `shops/${shopId}/sizechart_templates`);
      const q = query(templatesRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const templatesData = [];
      querySnapshot.forEach((doc) => {
        templatesData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setTemplates(templatesData);
      
      // Set default template if available
      if (templatesData.length > 0 && !selectedTemplate) {
        setSelectedTemplate(templatesData[0]);
        loadTemplateSettings(templatesData[0]);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      setError('Failed to load templates. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Load generated charts from Firestore
  const loadGeneratedCharts = async () => {
    try {
      setIsLoading(true);
      
      const chartsRef = collection(db, `shops/${shopId}/generated_sizecharts`);
      const q = query(chartsRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const charts = [];
      querySnapshot.forEach((doc) => {
        // Log each chart for debugging
        console.log('Loaded chart from Firestore:', doc.id, doc.data());
        charts.push({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt
        });
      });
      
      setGeneratedCharts(charts);
    } catch (error) {
      console.error('Error loading generated charts:', error);
      setError('Failed to load generated charts. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Load template settings
  const loadTemplateSettings = (template) => {
    if (!template) return;
    
    setTemplateName(template.name || '');
    setTemplateDescription(template.description || '');
    setHeaderText(template.headerText || '');
    setFooterText(template.footerText || '');
    setInfoText(template.infoText || '');
    setShowHeaderImage(template.showHeaderImage || false);
    setHeaderImageUrl(template.headerImageUrl || '');
    setShowFooterImage(template.showFooterImage || false);
    setFooterImageUrl(template.footerImageUrl || '');
    
    // Set language if available
    setSelectedLanguage(template.language || 'en');
    
    // Set garment template settings
    setGarmentType(template.garmentType || 'dress');
    setFitType(template.fitType || 'regular');
    setStretchLevel(template.stretchLevel || 'none');
    setMaterialInfo(template.materialInfo || '');
    setShowMeasurementDiagram(template.showMeasurementDiagram !== false);
    
    // Debug the template.chartStyle object
    console.log('Loading template:', template.name);
    console.log('Template chartStyle:', template.chartStyle);
    
    // Set chart style with defaults for any missing properties
    const defaultStyle = {
      fontFamily: 'sans-serif',
      chartTitleColor: '#212b36',
      
      // Size Chart Table styles (first in preview)
      borderColor: '#dfe3e8',
      headerBgColor: '#f4f6f8',
      headerTextColor: '#212b36',
      cellTextColor: '#212b36',
      alternateRowTextColor: '#212b36',
      rowAlternateColor: '#f9fafb',
      
      // How to Measure section styles (second in preview)
      measurementBgColor: '#f4f6f8',
      measurementHeadingColor: '#212b36',
      measurementTextColor: '#637381',
      measurementNumberBgColor: '#0068d9',
      measurementNumberColor: '#ffffff',
      
      // Fit Guide specific styles (third in preview)
      fitGuideHeadingColor: '#212b36',
      fitGuideTextColor: '#637381',
      fitGuideAccentColor: '#0068d9',
      fitGuideCardBgColor: '#ffffff',
      fitGuideTagBgColor: '#f2f7fe',
      fitGuideTagTextColor: '#0068d9',
      
      // Fit Guide Visualizer styles (fourth in preview)
      fitGuideBgColor: '#f4f6f8',
      fitGuideDotColor: '#000000',
      fitGuideTrackColor: '#e6e6e6',
      fitGuideLabelColor: '#637381',
      fitGuideIndicatorShape: 'dot',
      fitGuideIndicatorSize: 16,
      fitGuideIndicatorOffset: 0,
      notesBgColor: '#f9fafb',
      notesHeadingColor: '#212b36',
      notesTextColor: '#637381',
      
      // Footer and info styles (sixth in preview)
      footerTextColor: '#637381',
      infoTextColor: '#637381',
      infoBgColor: '#f9fafb'
    };
    
    // Merge template style with defaults
    setChartStyle({
      ...defaultStyle,
      ...(template.chartStyle || {})
    });
  };
  
  // Save template
  const saveTemplate = async () => {
    try {
      setIsLoading(true);
      
      const templateData = {
        name: templateName,
        description: templateDescription,
        headerText,
        footerText,
        infoText,
        showHeaderImage,
        headerImageUrl,
        showFooterImage,
        footerImageUrl,
        chartStyle,
        language: selectedLanguage,
        garmentType,
        fitType,
        stretchLevel,
        materialInfo,
        showMeasurementDiagram,
        updatedAt: serverTimestamp()
      };
      
      if (selectedTemplate) {
        // Update existing template
        const templateRef = doc(db, `shops/${shopId}/sizechart_templates`, selectedTemplate.id);
        await updateDoc(templateRef, templateData);
      } else {
        // Create new template
        templateData.createdAt = serverTimestamp();
        templateData.createdBy = userId;
        
        const templatesRef = collection(db, `shops/${shopId}/sizechart_templates`);
        await addDoc(templatesRef, templateData);
      }
      
      // Reload templates
      await loadTemplates();
      
      // Show success message
      alert('Template saved successfully!');
    } catch (error) {
      console.error('Error saving template:', error);
      setError('Failed to save template. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Delete template
  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
      return;
    }
    
    try {
      setIsLoading(true);
      
      const templateRef = doc(db, `shops/${shopId}/sizechart_templates`, templateId);
      await deleteDoc(templateRef);
      
      // Reload templates
      await loadTemplates();
      
      // Reset selected template if it was deleted
      if (selectedTemplate && selectedTemplate.id === templateId) {
        setSelectedTemplate(null);
        
        // Reset form fields
        setTemplateName('');
        setTemplateDescription('');
        setHeaderText('');
        setFooterText('');
        setInfoText('');
        setShowHeaderImage(false);
        setHeaderImageUrl('');
        setShowFooterImage(false);
        setFooterImageUrl('');
        setChartStyle({
          borderColor: '#dfe3e8',
          headerBgColor: '#f4f6f8',
          rowAlternateColor: '#f9fafb',
          fontFamily: 'sans-serif',
          fontSize: '14px'
        });
      }
      
      // Show success message
      alert('Template deleted successfully!');
    } catch (error) {
      console.error('Error deleting template:', error);
      setError('Failed to delete template. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Preview chart
  const handlePreviewChart = async (chart) => {
    try {
      console.log('Previewing chart:', chart);
      
      // Ensure we have the correct fitType and stretchLevel values
      // This is important for the FitGuideVisualizer component
      const updatedChart = {
        ...chart,
        // Ensure fitType and stretchLevel are at the top level of the chart object
        // Check all possible locations where these values might be stored
        fitType: chart.fitType || 
                chart.sizechart?.fit_type || 
                chart.sizechart?.sizechart?.fit_type || 
                'regular',
        stretchLevel: chart.stretchLevel || 
                     chart.sizechart?.stretch_level || 
                     chart.sizechart?.sizechart?.stretch_level || 
                     'none'
      };
      
      console.log('Updated chart with fit and stretch values:', updatedChart);
      console.log('Fit type source:', 
                 chart.fitType ? 'chart.fitType' : 
                 chart.sizechart?.fit_type ? 'chart.sizechart.fit_type' : 
                 chart.sizechart?.sizechart?.fit_type ? 'chart.sizechart.sizechart.fit_type' : 
                 'default');
      console.log('Stretch level source:', 
                 chart.stretchLevel ? 'chart.stretchLevel' : 
                 chart.sizechart?.stretch_level ? 'chart.sizechart.stretch_level' : 
                 chart.sizechart?.sizechart?.stretch_level ? 'chart.sizechart.sizechart.stretch_level' : 
                 'default');
      
      setPreviewChart(updatedChart);
      setIsLoading(true);
      
      // Reset edit mode
      setIsEditMode(false);
      setEditableChart(null);
      setOriginalChart(null);
      
      // Load the saved language if available
      if (chart.language) {
        console.log('Setting language from chart:', chart.language);
        setSelectedLanguage(chart.language);
      }
      
      // If the chart has a templateId, fetch the latest template settings
      if (chart.templateId) {
        console.log(`Chart has templateId: ${chart.templateId}, fetching latest template settings`);
        try {
          const templateRef = doc(db, `shops/${shopId}/sizechart_templates`, chart.templateId);
          const templateDoc = await getDoc(templateRef);
          
          if (templateDoc.exists()) {
            const templateData = templateDoc.data();
            console.log('Found template:', templateData);
            
          // Load the latest template settings
          setHeaderText(templateData.headerText || '');
          setFooterText(templateData.footerText || '');
          setInfoText(templateData.infoText || '');
          setShowHeaderImage(templateData.showHeaderImage || false);
          setHeaderImageUrl(templateData.headerImageUrl || '');
          setShowFooterImage(templateData.showFooterImage || false);
          setFooterImageUrl(templateData.footerImageUrl || '');
          setChartStyle(templateData.chartStyle || {
            borderColor: '#dfe3e8',
            headerBgColor: '#f4f6f8',
            rowAlternateColor: '#f9fafb',
            fontFamily: 'sans-serif',
            fontSize: '14px',
            // Add default Fit Guide styles if they don't exist in the template
            fitGuideBgColor: templateData.chartStyle?.fitGuideBgColor || '#f4f6f8',
            fitGuideAccentColor: templateData.chartStyle?.fitGuideAccentColor || '#0068d9',
            fitGuideCardBgColor: templateData.chartStyle?.fitGuideCardBgColor || '#ffffff',
            fitGuideTagBgColor: templateData.chartStyle?.fitGuideTagBgColor || '#f2f7fe',
            fitGuideTagTextColor: templateData.chartStyle?.fitGuideTagTextColor || '#0068d9'
          });
          } else {
            console.log('Template not found, using stored template settings');
            // If the template doesn't exist anymore, use the stored template settings
            if (chart.templateSettings) {
              applyStoredTemplateSettings(chart.templateSettings);
            } else {
              console.log('No stored template settings, using defaults');
              applyDefaultSettings();
            }
          }
        } catch (error) {
          console.error('Error fetching template:', error);
          // If there's an error fetching the template, use the stored template settings
          if (chart.templateSettings) {
            applyStoredTemplateSettings(chart.templateSettings);
          } else {
            applyDefaultSettings();
          }
        }
      } else if (chart.templateSettings) {
        // Use stored template settings if no template ID but has templateSettings
        console.log('Using stored template settings');
        applyStoredTemplateSettings(chart.templateSettings);
      } else {
        // Use default chart style if no template is available
        console.log('No template associated with this chart, using defaults');
        applyDefaultSettings();
      }
      
      // Set garment-specific template information if available
      if (chart.garmentType) {
        console.log(`Setting garment type to ${chart.garmentType}`);
        setGarmentType(chart.garmentType);
      }
      
      if (chart.fitType) {
        console.log(`Setting fit type to ${chart.fitType}`);
        setFitType(chart.fitType);
      }
      
      if (chart.stretchLevel) {
        console.log(`Setting stretch level to ${chart.stretchLevel}`);
        setStretchLevel(chart.stretchLevel);
      }
      
      if (chart.materialInfo) {
        console.log(`Setting material info to ${chart.materialInfo}`);
        setMaterialInfo(chart.materialInfo);
      }
      
      if (chart.showMeasurementDiagram !== undefined) {
        console.log(`Setting show measurement diagram to ${chart.showMeasurementDiagram}`);
        setShowMeasurementDiagram(chart.showMeasurementDiagram);
      }
      
      // Open the modal
      setIsChartPreviewModalOpen(true);
    } catch (error) {
      console.error('Error previewing chart:', error);
      setError(`Failed to preview chart: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
    
    // Helper function to apply the stored template settings
    function applyStoredTemplateSettings() {
      if (!chart.templateSettings) {
        applyDefaultSettings();
        return;
      }
      
      setHeaderText(chart.templateSettings.headerText || '');
      setFooterText(chart.templateSettings.footerText || '');
      setInfoText(chart.templateSettings.infoText || '');
      setShowHeaderImage(chart.templateSettings.showHeaderImage || false);
      setHeaderImageUrl(chart.templateSettings.headerImageUrl || '');
      setShowFooterImage(chart.templateSettings.showFooterImage || false);
      setFooterImageUrl(chart.templateSettings.footerImageUrl || '');
      setChartStyle(chart.templateSettings.chartStyle || {
        borderColor: '#dfe3e8',
        headerBgColor: '#f4f6f8',
        rowAlternateColor: '#f9fafb',
        fontFamily: 'sans-serif',
        fontSize: '14px',
        // Add default Fit Guide styles if they don't exist in the stored settings
        fitGuideBgColor: chart.templateSettings.chartStyle?.fitGuideBgColor || '#f4f6f8',
        fitGuideAccentColor: chart.templateSettings.chartStyle?.fitGuideAccentColor || '#0068d9',
        fitGuideCardBgColor: chart.templateSettings.chartStyle?.fitGuideCardBgColor || '#ffffff',
        fitGuideTagBgColor: chart.templateSettings.chartStyle?.fitGuideTagBgColor || '#f2f7fe',
        fitGuideTagTextColor: chart.templateSettings.chartStyle?.fitGuideTagTextColor || '#0068d9'
      });
    }
    
    // Helper function to apply default settings
    function applyDefaultSettings() {
      setHeaderText('');
      setFooterText('');
      setInfoText('');
      setShowHeaderImage(false);
      setHeaderImageUrl('');
      setShowFooterImage(false);
      setFooterImageUrl('');
      setChartStyle({
        borderColor: '#dfe3e8',
        headerBgColor: '#f4f6f8',
        rowAlternateColor: '#f9fafb',
        fontFamily: 'sans-serif',
        fontSize: '14px',
        // Default Fit Guide styles
        fitGuideBgColor: '#f4f6f8',
        fitGuideAccentColor: '#0068d9',
        fitGuideCardBgColor: '#ffffff',
        fitGuideTagBgColor: '#f2f7fe',
        fitGuideTagTextColor: '#0068d9'
      });
    }
    
    setIsChartPreviewModalOpen(true);
  };
  
  // Delete chart
  const deleteChart = async (chartId) => {
    if (!window.confirm('Are you sure you want to delete this chart? This action cannot be undone.')) {
      return;
    }
    
    try {
      setIsLoading(true);
      
      const chartRef = doc(db, `shops/${shopId}/generated_sizecharts`, chartId);
      await deleteDoc(chartRef);
      
      // Reload charts
      await loadGeneratedCharts();
      
      // Show success message
      alert('Chart deleted successfully!');
    } catch (error) {
      console.error('Error deleting chart:', error);
      setError('Failed to delete chart. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Export chart as image - opens the export options modal
  const exportChartAsImage = async (chart) => {
    try {
      // Store the chart to export and set the operation type
      setChartToExport(chart);
      setExportOperation('export');
      
      // Open the export options modal
      setIsExportOptionsModalOpen(true);
    } catch (error) {
      console.error('Error preparing chart for export:', error);
      alert('Failed to prepare chart for export. Please try again.');
    }
  };
  
  // Handle the actual export after options are selected
  const handleExportWithOptions = async (options, filename) => {
    try {
      setIsLoading(true);
      setIsExportOptionsModalOpen(false);
      
      // First, check if the chart has a template ID and fetch the latest template settings
      let chartStyleValue = {
        borderColor: '#dfe3e8',
        headerBgColor: '#f4f6f8',
        rowAlternateColor: '#f9fafb',
        fontFamily: 'sans-serif',
        fontSize: '14px',
        fitGuideBgColor: '#f4f6f8',
        fitGuideAccentColor: '#0068d9',
        fitGuideCardBgColor: '#ffffff',
        fitGuideTagBgColor: '#f2f7fe',
        fitGuideTagTextColor: '#0068d9'
      };
      
      // Variables to store template data
      let latestTemplate = null;
      
      // If the chart has a template ID, fetch the latest template settings from Firestore
      if (chartToExport.templateId) {
        console.log('Fetching latest template settings for export, template ID:', chartToExport.templateId);
        const templateRef = doc(db, `shops/${shopId}/sizechart_templates`, chartToExport.templateId);
        const templateDoc = await getDoc(templateRef);
        
        if (templateDoc.exists()) {
          // Use the latest template settings from Firestore
          latestTemplate = templateDoc.data();
          console.log('Found latest template settings for export:', latestTemplate);
          
          // Use the template's chartStyle
          if (latestTemplate.chartStyle) {
            chartStyleValue = latestTemplate.chartStyle;
          }
        } else if (chartToExport.chartStyle) {
          // Fall back to chart's stored style
          chartStyleValue = chartToExport.chartStyle;
        }
      } else if (chartToExport.chartStyle) {
        // Use chart's stored style if no template ID
        chartStyleValue = chartToExport.chartStyle;
      }
      
      // Extract text fields from the latest template if available
      let headerTextValue = '';
      let footerTextValue = '';
      let infoTextValue = '';
      let showHeaderImageValue = false;
      let headerImageUrlValue = '';
      let showFooterImageValue = false;
      let footerImageUrlValue = '';
      
      // First check if we have the latest template data from the fetch above
      if (latestTemplate) {
        console.log('Using text fields from latest template:', {
          headerText: latestTemplate.headerText,
          footerText: latestTemplate.footerText,
          infoText: latestTemplate.infoText
        });
        
        // Extract text fields from the latest template
        headerTextValue = latestTemplate.headerText || '';
        footerTextValue = latestTemplate.footerText || '';
        infoTextValue = latestTemplate.infoText || '';
        showHeaderImageValue = latestTemplate.showHeaderImage || false;
        headerImageUrlValue = latestTemplate.headerImageUrl || '';
        showFooterImageValue = latestTemplate.showFooterImage || false;
        footerImageUrlValue = latestTemplate.footerImageUrl || '';
      } 
      // If no latest template, try to get from templateSettings
      else if (chartToExport.templateSettings) {
        console.log('Using text fields from chart template settings:', {
          headerText: chartToExport.templateSettings.headerText,
          footerText: chartToExport.templateSettings.footerText,
          infoText: chartToExport.templateSettings.infoText
        });
        
        // Extract text fields from the chart's template settings
        headerTextValue = chartToExport.templateSettings.headerText || '';
        footerTextValue = chartToExport.templateSettings.footerText || '';
        infoTextValue = chartToExport.templateSettings.infoText || '';
        showHeaderImageValue = chartToExport.templateSettings.showHeaderImage || false;
        headerImageUrlValue = chartToExport.templateSettings.headerImageUrl || '';
        showFooterImageValue = chartToExport.templateSettings.showFooterImage || false;
        footerImageUrlValue = chartToExport.templateSettings.footerImageUrl || '';
      }
      
      // Prepare the chart data for export, ensuring text fields are included
      const chartToExportWithText = {
        ...chartToExport,
        // Include text fields from the template if available
        headerText: chartToExport.headerText || headerTextValue,
        footerText: chartToExport.footerText || footerTextValue,
        infoText: chartToExport.infoText || infoTextValue,
        // Include image settings
        showHeaderImage: chartToExport.showHeaderImage || showHeaderImageValue,
        headerImageUrl: chartToExport.headerImageUrl || headerImageUrlValue,
        showFooterImage: chartToExport.showFooterImage || showFooterImageValue,
        footerImageUrl: chartToExport.footerImageUrl || footerImageUrlValue
      };
      
      console.log('Exporting chart with text fields:', {
        headerText: chartToExportWithText.headerText,
        footerText: chartToExportWithText.footerText,
        infoText: chartToExportWithText.infoText
      });
      
      // Use the export service to generate and download the image
      try {
        // Call the service function with the enhanced chart data and custom filename
        await exportChartService(chartToExportWithText, chartStyleValue, options, filename);
        alert('Chart exported successfully!');
      } catch (error) {
        console.error('Error exporting chart:', error);
        alert(`Failed to export chart: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error in export process:', error);
      alert(`Export failed: ${error.message}`);
      setIsLoading(false);
    }
  };
      
  // Upload chart to Shopify - opens the export options modal
  const uploadChartToShopify = async (chart) => {
    try {
      // Store the chart to export and set the operation type
      setChartToExport(chart);
      setExportOperation('upload');
      
      // Open the export options modal
      setIsExportOptionsModalOpen(true);
    } catch (error) {
      console.error('Error preparing chart for Shopify upload:', error);
      alert('Failed to prepare chart for Shopify upload. Please try again.');
    }
  };
  
  // Handle the actual upload to Shopify after options are selected
  const handleUploadWithOptions = async (options) => {
    try {
      setIsLoading(true);
      setIsExportOptionsModalOpen(false);
      
      // First, check if the chart has a template ID and fetch the latest template settings
      let chartStyleValue = {
        borderColor: '#dfe3e8',
        headerBgColor: '#f4f6f8',
        rowAlternateColor: '#f9fafb',
        fontFamily: 'sans-serif',
        fontSize: '14px',
        fitGuideBgColor: '#f4f6f8',
        fitGuideAccentColor: '#0068d9',
        fitGuideCardBgColor: '#ffffff',
        fitGuideTagBgColor: '#f2f7fe',
        fitGuideTagTextColor: '#0068d9'
      };
      
      // Variables to store template data
      let latestTemplate = null;
      
      // If the chart has a template ID, fetch the latest template settings from Firestore
      if (chartToExport.templateId) {
        console.log('Fetching latest template settings for Shopify upload, template ID:', chartToExport.templateId);
        const templateRef = doc(db, `shops/${shopId}/sizechart_templates`, chartToExport.templateId);
        const templateDoc = await getDoc(templateRef);
        
        if (templateDoc.exists()) {
          // Use the latest template settings from Firestore
          latestTemplate = templateDoc.data();
          console.log('Found latest template settings for Shopify upload:', latestTemplate);
          
          // Use the template's chartStyle
          if (latestTemplate.chartStyle) {
            chartStyleValue = latestTemplate.chartStyle;
          }
        } else if (chartToExport.chartStyle) {
          // Fall back to chart's stored style
          chartStyleValue = chartToExport.chartStyle;
        }
      } else if (chartToExport.chartStyle) {
        // Use chart's stored style if no template ID
        chartStyleValue = chartToExport.chartStyle;
      }
      
      // Extract text fields from the latest template if available
      let headerTextValue = '';
      let footerTextValue = '';
      let infoTextValue = '';
      let showHeaderImageValue = false;
      let headerImageUrlValue = '';
      let showFooterImageValue = false;
      let footerImageUrlValue = '';
      
      // First check if we have the latest template data from the fetch above
      if (latestTemplate) {
        console.log('Using text fields from latest template for Shopify upload:', {
          headerText: latestTemplate.headerText,
          footerText: latestTemplate.footerText,
          infoText: latestTemplate.infoText
        });
        
        // Extract text fields from the latest template
        headerTextValue = latestTemplate.headerText || '';
        footerTextValue = latestTemplate.footerText || '';
        infoTextValue = latestTemplate.infoText || '';
        showHeaderImageValue = latestTemplate.showHeaderImage || false;
        headerImageUrlValue = latestTemplate.headerImageUrl || '';
        showFooterImageValue = latestTemplate.showFooterImage || false;
        footerImageUrlValue = latestTemplate.footerImageUrl || '';
      } 
      // If no latest template, try to get from templateSettings
      else if (chartToExport.templateSettings) {
        console.log('Using text fields from chart template settings for Shopify upload:', {
          headerText: chartToExport.templateSettings.headerText,
          footerText: chartToExport.templateSettings.footerText,
          infoText: chartToExport.templateSettings.infoText
        });
        
        // Extract text fields from the chart's template settings
        headerTextValue = chartToExport.templateSettings.headerText || '';
        footerTextValue = chartToExport.templateSettings.footerText || '';
        infoTextValue = chartToExport.templateSettings.infoText || '';
        showHeaderImageValue = chartToExport.templateSettings.showHeaderImage || false;
        headerImageUrlValue = chartToExport.templateSettings.headerImageUrl || '';
        showFooterImageValue = chartToExport.templateSettings.showFooterImage || false;
        footerImageUrlValue = chartToExport.templateSettings.footerImageUrl || '';
      }
      
      // Prepare the chart data for upload, ensuring text fields are included
      const chartToUploadWithText = {
        ...chartToExport,
        // Include text fields from the template if available
        headerText: chartToExport.headerText || headerTextValue,
        footerText: chartToExport.footerText || footerTextValue,
        infoText: chartToExport.infoText || infoTextValue,
        // Include image settings
        showHeaderImage: chartToExport.showHeaderImage || showHeaderImageValue,
        headerImageUrl: chartToExport.headerImageUrl || headerImageUrlValue,
        showFooterImage: chartToExport.showFooterImage || showFooterImageValue,
        footerImageUrl: chartToExport.footerImageUrl || footerImageUrlValue
      };
      
      console.log('Uploading chart to Shopify with text fields:', {
        headerText: chartToUploadWithText.headerText,
        footerText: chartToUploadWithText.footerText,
        infoText: chartToUploadWithText.infoText
      });
      
      // Use the export service to generate and upload the image to Shopify
      try {
        // Get the access token from the current shop context or from props
        let shopAccessToken = accessToken;
        
        // If no access token provided in props, try to get it from the current shop context
        if (!shopAccessToken && currentShop && currentShop.accessToken) {
          shopAccessToken = currentShop.accessToken;
          console.log('Using access token from shop context');
        }
        
        // If still no access token, try localStorage as a last resort
        if (!shopAccessToken) {
          try {
            const shopData = JSON.parse(localStorage.getItem('currentShop') || '{}');
            shopAccessToken = shopData.accessToken;
            console.log('Using access token from localStorage:', shopAccessToken ? 'Found' : 'Not found');
          } catch (error) {
            console.error('Error getting access token from localStorage:', error);
          }
        }
        
        if (!shopAccessToken) {
          throw new Error('Shop access token not available. Please refresh the page or check your login status.');
        }
        
        const uploadResult = await uploadChartService(chartToUploadWithText, chartStyleValue, options, shopId, shopAccessToken);
        
        if (uploadResult.success) {
          // Get the image URL from the result (handle both url and imageUrl properties)
          const imageUrl = uploadResult.imageUrl || uploadResult.url;
          
          // Always show a modal, regardless of whether we have an image URL
          console.log('Upload result:', uploadResult);
          setIsLoading(false);
          
          // Determine the appropriate modal content based on the upload result
          let modalTitle = 'Size Chart Uploaded Successfully!';
          let modalMessage = '';
          let shopifyAdminUrl = '';
          
          if (uploadResult.message) {
            modalMessage = uploadResult.message;
          }
          
          if (uploadResult.note) {
            modalMessage += ' ' + uploadResult.note;
          }
          
          if (uploadResult.shopifyAdmin) {
            shopifyAdminUrl = uploadResult.shopifyAdmin;
          }
          
          if (!modalMessage) {
            modalMessage = 'Your size chart has been uploaded to Shopify.';
          }
          
          // Create modal content
          const modalContent = `
            <div style="padding: 20px;">
              <h3>${modalTitle}</h3>
              <p>${modalMessage}</p>
              ${shopifyAdminUrl ? `<p>You can view your file in the <a href="${shopifyAdminUrl}" target="_blank" style="color: #008060; text-decoration: underline;">Shopify Admin Files Section</a>.</p>` : ''}
              <p>File ID: ${uploadResult.fileId || 'Not available'}</p>
              ${uploadResult.fileId ? `<div style="margin-top: 15px;">
                <button id="copy-id-btn" style="padding: 8px 16px; background-color: #008060; color: white; border: none; border-radius: 4px; cursor: pointer;">Copy File ID</button>
              </div>` : ''}
            </div>
          `;
          
          // Create a modal element
          const modalContainer = document.createElement('div');
          modalContainer.style.position = 'fixed';
          modalContainer.style.top = '0';
          modalContainer.style.left = '0';
          modalContainer.style.width = '100%';
          modalContainer.style.height = '100%';
          modalContainer.style.backgroundColor = 'rgba(0,0,0,0.5)';
          modalContainer.style.display = 'flex';
          modalContainer.style.justifyContent = 'center';
          modalContainer.style.alignItems = 'center';
          modalContainer.style.zIndex = '9999';
          
          const modalBox = document.createElement('div');
          modalBox.style.backgroundColor = 'white';
          modalBox.style.borderRadius = '8px';
          modalBox.style.maxWidth = '600px';
          modalBox.style.width = '90%';
          modalBox.style.maxHeight = '80vh';
          modalBox.style.overflowY = 'auto';
          modalBox.style.position = 'relative';
          
          const closeButton = document.createElement('button');
          closeButton.innerHTML = '&times;';
          closeButton.style.position = 'absolute';
          closeButton.style.top = '10px';
          closeButton.style.right = '10px';
          closeButton.style.border = 'none';
          closeButton.style.background = 'none';
          closeButton.style.fontSize = '24px';
          closeButton.style.cursor = 'pointer';
          closeButton.onclick = () => document.body.removeChild(modalContainer);
          
          modalBox.innerHTML = modalContent;
          modalBox.appendChild(closeButton);
          modalContainer.appendChild(modalBox);
          document.body.appendChild(modalContainer);
          
          // Add event listener to the copy button if it exists
          setTimeout(() => {
            const copyBtn = document.getElementById('copy-id-btn');
            if (copyBtn && uploadResult.fileId) {
              copyBtn.addEventListener('click', () => {
                navigator.clipboard.writeText(uploadResult.fileId)
                  .then(() => {
                    copyBtn.textContent = 'Copied!';
                    setTimeout(() => {
                      copyBtn.textContent = 'Copy File ID';
                    }, 2000);
                  })
                  .catch(err => {
                    console.error('Could not copy text: ', err);
                    alert('Failed to copy File ID. Please select and copy it manually.');
                  });
              });
            }
          }, 100);
        }
      } catch (error) {
        console.error('Error uploading chart to Shopify:', error);
        setIsLoading(false);
        
        // Show error in a modal instead of an alert
        const errorModalContent = `
          <div style="padding: 20px;">
            <h3>Upload Failed</h3>
            <p>There was an error uploading your size chart to Shopify:</p>
            <div style="margin: 15px 0; padding: 10px; background-color: #fff1f0; border-radius: 4px; border: 1px solid #ffccc7; color: #cf1322;">
              ${error.message}
            </div>
            <p>Please try again or contact support if the problem persists.</p>
          </div>
        `;
        
        // Create a modal element for the error
        const errorModalContainer = document.createElement('div');
        errorModalContainer.style.position = 'fixed';
        errorModalContainer.style.top = '0';
        errorModalContainer.style.left = '0';
        errorModalContainer.style.width = '100%';
        errorModalContainer.style.height = '100%';
        errorModalContainer.style.backgroundColor = 'rgba(0,0,0,0.5)';
        errorModalContainer.style.display = 'flex';
        errorModalContainer.style.justifyContent = 'center';
        errorModalContainer.style.alignItems = 'center';
        errorModalContainer.style.zIndex = '9999';
        
        const errorModalBox = document.createElement('div');
        errorModalBox.style.backgroundColor = 'white';
        errorModalBox.style.borderRadius = '8px';
        errorModalBox.style.maxWidth = '600px';
        errorModalBox.style.width = '90%';
        errorModalBox.style.maxHeight = '80vh';
        errorModalBox.style.overflowY = 'auto';
        errorModalBox.style.position = 'relative';
        
        const errorCloseButton = document.createElement('button');
        errorCloseButton.innerHTML = '&times;';
        errorCloseButton.style.position = 'absolute';
        errorCloseButton.style.top = '10px';
        errorCloseButton.style.right = '10px';
        errorCloseButton.style.border = 'none';
        errorCloseButton.style.background = 'none';
        errorCloseButton.style.fontSize = '24px';
        errorCloseButton.style.cursor = 'pointer';
        errorCloseButton.onclick = () => document.body.removeChild(errorModalContainer);
        
        errorModalBox.innerHTML = errorModalContent;
        errorModalBox.appendChild(errorCloseButton);
        errorModalContainer.appendChild(errorModalBox);
        document.body.appendChild(errorModalContainer);
      }
    } catch (error) {
      console.error('Error in Shopify upload process:', error);
      alert(`Shopify upload failed: ${error.message}`);
      setIsLoading(false);
    }
  };
      

  
  // Render template design tab
  const renderTemplateDesign = () => {
    return (
      <Card>
        <Card.Section>
          <TextContainer>
            <Text variant="headingMd">Template Design</Text>
            <Text variant="bodyMd">Create and customize templates for your size charts.</Text>
          </TextContainer>
        </Card.Section>
        
        <Card.Section>
          <FormLayout>
            <LegacyStack distribution="equalSpacing">
              <Select
                label="Template"
                options={[
                  { label: 'Create New Template', value: 'new' },
                  ...templates.map(template => ({
                    label: template.name,
                    value: template.id
                  }))
                ]}
                value={selectedTemplate ? selectedTemplate.id : 'new'}
                onChange={(value) => {
                  if (value === 'new') {
                    setSelectedTemplate(null);
                    // Reset form fields
                    setTemplateName('');
                    setTemplateDescription('');
                    setHeaderText('');
                    setFooterText('');
                    setInfoText('');
                    setShowHeaderImage(false);
                    setHeaderImageUrl('');
                    setShowFooterImage(false);
                    setFooterImageUrl('');
                    setChartStyle({
                      borderColor: '#dfe3e8',
                      headerBgColor: '#f4f6f8',
                      rowAlternateColor: '#f9fafb',
                      fontFamily: 'sans-serif',
                      fontSize: '14px'
                    });
                  } else {
                    const template = templates.find(t => t.id === value);
                    setSelectedTemplate(template);
                    loadTemplateSettings(template);
                  }
                }}
              />
              <div>
                <Button onClick={saveTemplate} primary loading={isLoading}>Save Template</Button>
                {selectedTemplate && (
                  <Button onClick={() => deleteTemplate(selectedTemplate.id)} destructive>Delete</Button>
                )}
              </div>
            </LegacyStack>
            
            <TextField
              label="Template Name"
              value={templateName}
              onChange={setTemplateName}
              placeholder="e.g., Standard Size Chart Template"
              helpText="Give your template a descriptive name"
            />
            
            <TextField
              label="Template Description"
              value={templateDescription}
              onChange={setTemplateDescription}
              placeholder="e.g., Standard template for clothing size charts"
              multiline={2}
              helpText="Provide a brief description of this template"
            />
          </FormLayout>
        </Card.Section>
        
        <Card.Section title="Header Content">
          <FormLayout>
            <TextField
              label="Header Text"
              value={headerText}
              onChange={setHeaderText}
              placeholder="e.g., Size Chart for [Product Name]"
              multiline={2}
              helpText="Text to display above the size chart"
            />
            
            <ChoiceList
              title="Header Image"
              choices={[
                {label: 'Show header image', value: 'show'},
                {label: 'Hide header image', value: 'hide'}
              ]}
              selected={showHeaderImage ? ['show'] : ['hide']}
              onChange={(selected) => {
                setShowHeaderImage(selected.includes('show'));
              }}
            />
            
            {showHeaderImage && (
              <TextField
                label="Header Image URL"
                value={headerImageUrl}
                onChange={setHeaderImageUrl}
                placeholder="https://example.com/header-image.jpg"
                helpText="URL of the image to display above the size chart"
              />
            )}
          </FormLayout>
        </Card.Section>
        
        <Card.Section title="Translations">
          <FormLayout>
            <Select
              label="Language"
              options={[
                {label: 'English', value: 'en'},
                {label: 'German (Deutsch)', value: 'de'},
                {label: 'French (Français)', value: 'fr'},
                {label: 'Spanish (Español)', value: 'es'},
                {label: 'Italian (Italiano)', value: 'it'},
                {label: 'Dutch (Nederlands)', value: 'nl'},
                {label: 'Portuguese (Português)', value: 'pt'},
                {label: 'Japanese (日本語)', value: 'ja'},
                {label: 'Chinese (中文)', value: 'zh'}
              ]}
              value={selectedLanguage}
              onChange={setSelectedLanguage}
              helpText="Select the language for your size chart. This will translate all fixed elements like section titles, fit types, and measurement labels."
            />
          </FormLayout>
        </Card.Section>
        
        <Card.Section title="Footer Content">
          <FormLayout>
            <TextField
              label="Footer Text"
              value={footerText}
              onChange={setFooterText}
              placeholder="e.g., Measurements may vary by 1-2 inches"
              multiline={2}
              helpText="Text to display below the size chart"
            />
            
            <TextField
              label="Info Text"
              value={infoText}
              onChange={setInfoText}
              placeholder="e.g., For more information, please contact customer service"
              multiline={2}
              helpText="Additional information to display below the footer"
            />
            
            <ChoiceList
              title="Footer Image"
              choices={[
                {label: 'Show footer image', value: 'show'},
                {label: 'Hide footer image', value: 'hide'}
              ]}
              selected={showFooterImage ? ['show'] : ['hide']}
              onChange={(selected) => {
                setShowFooterImage(selected.includes('show'));
              }}
            />
            
            {showFooterImage && (
              <TextField
                label="Footer Image URL"
                value={footerImageUrl}
                onChange={setFooterImageUrl}
                placeholder="https://example.com/footer-image.jpg"
                helpText="URL of the image to display below the size chart"
              />
            )}
          </FormLayout>
        </Card.Section>
        
        <Card.Section title="Chart Style">
          <FormLayout>
            {/* Global Styles */}
            <Text variant="headingMd">Global Styles</Text>
            
            {/* Google Fonts Dropdown with Search */}
            <div>
              <Text variant="bodyMd" as="p" fontWeight="semibold">Font Family</Text>
              <div style={{ marginTop: '4px', marginBottom: '4px' }}>
                <TextField
                  placeholder="Search fonts..."
                  value={fontSearchQuery}
                  onChange={(value) => {
                    setFontSearchQuery(value);
                    // Filter from the comprehensive list of all Google Fonts
                    if (allGoogleFonts.length > 0) {
                      const filtered = allGoogleFonts.filter(font => 
                        font.label.toLowerCase().includes(value.toLowerCase())
                      );
                      setFilteredFonts(filtered.slice(0, 100)); // Limit to 100 results for performance
                    } else {
                      // Fallback to the initial list if the API call failed
                      setFilteredFonts(
                        googleFonts.filter(font => 
                          font.label.toLowerCase().includes(value.toLowerCase())
                        )
                      );
                    }
                  }}
                />
              </div>
              
              {isLoadingFonts ? (
                <div style={{ textAlign: 'center', padding: '10px' }}>
                  <Spinner size="small" />
                  <div style={{ marginTop: '8px' }}>
                    <Text variant="bodyMd">Loading fonts...</Text>
                  </div>
                </div>
              ) : (
                <Select
                  options={filteredFonts.length > 0 ? filteredFonts : (allGoogleFonts.length > 0 ? allGoogleFonts.slice(0, 100) : googleFonts)}
                  value={chartStyle.fontFamily}
                  onChange={(value) => {
                    setChartStyle({...chartStyle, fontFamily: value});
                    
                    // Extract font name from the value (format: "Font Name", category)
                    const fontName = value.split('"')[1];
                    if (fontName && !['Sans-serif', 'Serif', 'Monospace'].includes(fontName)) {
                      // Add the font to the document head if it's not a system font
                      const link = document.createElement('link');
                      link.href = `https://fonts.googleapis.com/css2?family=${fontName.replace(/ /g, '+')}:wght@400;700&display=swap`;
                      link.rel = 'stylesheet';
                      document.head.appendChild(link);
                    }
                  }}
                  helpText="Choose from hundreds of Google Fonts"
                />
              )}
              
              {/* Font preview */}
              {chartStyle.fontFamily && (
                <div style={{ marginTop: '8px', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}>
                  <Text variant="bodySm" color="subdued">Font preview:</Text>
                  <div style={{ marginTop: '4px', fontFamily: chartStyle.fontFamily, fontSize: '16px' }}>
                    The quick brown fox jumps over the lazy dog.
                  </div>
                </div>
              )}
            </div>
            
            {/* Chart Title Color */}
            <ColorPickerField
              label="Chart Title Color"
              value={chartStyle.chartTitleColor}
              onChange={(value) => setChartStyle({...chartStyle, chartTitleColor: value})}
              helpText="Color for the chart title text"
            />

            {/* Translations Settings */}
            <Divider />
            <Text variant="headingMd">Translations</Text>
            
            <Select
              label="Chart Language"
              options={defaultTranslations.languages}
              onChange={(value) => setSelectedLanguage(value)}
              value={selectedLanguage}
              helpText="Select language for size chart labels and descriptions"
            />
            
            <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f4f6f8', borderRadius: '4px' }}>
              <Text variant="bodySm">
                The selected language will be used for all text in the size chart, including:
                <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                  <li>Measurement point labels (Bust, Waist, Hip, etc.)</li>
                  <li>Measurement descriptions</li>
                  <li>Fit types (Slim, Regular, Loose, etc.)</li>
                  <li>Section titles (Size Chart, How to Measure, etc.)</li>
                </ul>
              </Text>
            </div>
            
            {/* Fit & Stretch Guide Visualizer Settings */}
            <Divider />
            <Text variant="headingMd">Fit & Stretch Guide Visualizer Settings</Text>
            
            {/* Indicator Shape */}
            <Select
              label="Indicator Shape"
              options={[
                {label: 'Dot (Circle)', value: 'dot'},
                {label: 'Rectangle', value: 'rectangle'},
                {label: 'Triangle', value: 'triangle'},
                {label: 'Diamond', value: 'diamond'}
              ]}
              onChange={(value) => setChartStyle({...chartStyle, fitGuideIndicatorShape: value})}
              value={chartStyle.fitGuideIndicatorShape || 'dot'}
              helpText="Shape of the indicator on the fit guide visualizer"
            />
            
            {/* Indicator Size */}
            <TextField
              label="Indicator Size"
              type="number"
              value={(chartStyle.fitGuideIndicatorSize || 16).toString()}
              onChange={(value) => setChartStyle({...chartStyle, fitGuideIndicatorSize: parseInt(value) || 16})}
              min="8"
              max="32"
              helpText="Size of the indicator in pixels (8-32)"
            />
            
            {/* Vertical Position */}
            <TextField
              label="Vertical Position"
              type="number"
              value={(chartStyle.fitGuideIndicatorOffset || 0).toString()}
              onChange={(value) => setChartStyle({...chartStyle, fitGuideIndicatorOffset: parseInt(value) || 0})}
              min="-20"
              max="20"
              helpText="Adjust vertical position (-20 to +20, negative = up, positive = down)"
            />
            
            {/* Fit Guide Shape Color */}
            <ColorPickerField
              label="Shape Color"
              value={chartStyle.fitGuideDotColor}
              onChange={(value) => setChartStyle({...chartStyle, fitGuideDotColor: value})}
              helpText="Color of the shape in the fit guide visualizer"
            />
            
            {/* Fit Guide Track Color */}
            <ColorPickerField
              label="Track Color"
              value={chartStyle.fitGuideTrackColor}
              onChange={(value) => setChartStyle({...chartStyle, fitGuideTrackColor: value})}
              helpText="Color of the track/bar in the fit guide visualizer"
            />
            
            {/* Fit Guide Label Color */}
            <ColorPickerField
              label="Label Color"
              value={chartStyle.fitGuideLabelColor}
              onChange={(value) => setChartStyle({...chartStyle, fitGuideLabelColor: value})}
              helpText="Color of the labels in the fit guide visualizer"
            />

            {/* Size Chart Table Styles */}
            <Divider />
            <Text variant="headingMd">Size Chart Table Styles</Text>
            
            {/* Border Color */}
            <ColorPickerField
              label="Border Color"
              value={chartStyle.borderColor}
              onChange={(value) => setChartStyle({...chartStyle, borderColor: value})}
              helpText="Border color for the size chart table"
            />
            
            {/* Header Background Color */}
            <ColorPickerField
              label="Header Background Color"
              value={chartStyle.headerBgColor}
              onChange={(value) => setChartStyle({...chartStyle, headerBgColor: value})}
              helpText="Background color for the table header"
            />
            
            {/* Header Text Color */}
            <ColorPickerField
              label="Header Text Color"
              value={chartStyle.headerTextColor}
              onChange={(value) => setChartStyle({...chartStyle, headerTextColor: value})}
              helpText="Text color for the table header"
            />
            
            {/* Cell Text Color */}
            <ColorPickerField
              label="Cell Text Color"
              value={chartStyle.cellTextColor}
              onChange={(value) => setChartStyle({...chartStyle, cellTextColor: value})}
              helpText="Text color for the table cells"
            />
            
            {/* Alternate Row Text Color */}
            <ColorPickerField
              label="Alternate Row Text Color"
              value={chartStyle.alternateRowTextColor}
              onChange={(value) => setChartStyle({...chartStyle, alternateRowTextColor: value})}
              helpText="Text color for alternate rows in the table"
            />
            
            {/* Row Alternate Color */}
            <ColorPickerField
              label="Row Alternate Color"
              value={chartStyle.rowAlternateColor}
              onChange={(value) => setChartStyle({...chartStyle, rowAlternateColor: value})}
              helpText="Background color for alternate rows in the table"
            />
            
            {/* How to Measure Section Styles */}
            <Divider />
            <Text variant="headingMd">How to Measure Section Styles</Text>
            
            {/* Measurement Background Color */}
            <ColorPickerField
              label="Background Color"
              value={chartStyle.measurementBgColor}
              onChange={(value) => setChartStyle({...chartStyle, measurementBgColor: value})}
              helpText="Background color for the measurement diagram section"
            />
            
            {/* Measurement Heading Color */}
            <ColorPickerField
              label="Heading Color"
              value={chartStyle.measurementHeadingColor}
              onChange={(value) => setChartStyle({...chartStyle, measurementHeadingColor: value})}
              helpText="Color for headings in the measurement diagram section"
            />
            
            {/* Header Text Color */}
            <ColorPickerField
              label="Header Text Color"
              value={chartStyle.headerTextColor}
              onChange={(value) => setChartStyle({...chartStyle, headerTextColor: value})}
              helpText="Color code for table header text (hex format)"
            />
            
            {/* Chart Title Color */}
            <ColorPickerField
              label="Chart Title Color"
              value={chartStyle.chartTitleColor}
              onChange={(value) => setChartStyle({...chartStyle, chartTitleColor: value})}
              helpText="Color code for chart title/header text (hex format)"
            />
            
            {/* Footer Text Color */}
            <ColorPickerField
              label="Footer Text Color"
              value={chartStyle.footerTextColor}
              onChange={(value) => setChartStyle({...chartStyle, footerTextColor: value})}
              helpText="Color code for footer text (hex format)"
            />
            
            {/* Info Text Color */}
            <ColorPickerField
              label="Info Text Color"
              value={chartStyle.infoTextColor}
              onChange={(value) => setChartStyle({...chartStyle, infoTextColor: value})}
              helpText="Color code for info text (hex format)"
            />
            
            {/* Row Alternate Color */}
            <ColorPickerField
              label="Row Alternate Color"
              value={chartStyle.rowAlternateColor}
              onChange={(value) => setChartStyle({...chartStyle, rowAlternateColor: value})}
              helpText="Color code for alternate rows (hex format)"
            />
            
            {/* Cell Text Color */}
            <ColorPickerField
              label="Cell Text Color"
              value={chartStyle.cellTextColor}
              onChange={(value) => setChartStyle({...chartStyle, cellTextColor: value})}
              helpText="Color code for regular table cell text (hex format)"
            />
            
            {/* Alternate Row Text Color */}
            <ColorPickerField
              label="Alternate Row Text Color"
              value={chartStyle.alternateRowTextColor}
              onChange={(value) => setChartStyle({...chartStyle, alternateRowTextColor: value})}
              helpText="Color code for alternate row text (hex format)"
            />
            
            {/* Measurement Number Background Color */}
            <ColorPickerField
              label="Number Background Color"
              value={chartStyle.measurementNumberBgColor}
              onChange={(value) => setChartStyle({...chartStyle, measurementNumberBgColor: value})}
              helpText="Background color for measurement numbers"
            />
            
            {/* Measurement Number Text Color */}
            <ColorPickerField
              label="Number Text Color"
              value={chartStyle.measurementNumberColor}
              onChange={(value) => setChartStyle({...chartStyle, measurementNumberColor: value})}
              helpText="Text color for measurement numbers"
            />
            
            <div>
            {/* Fit Guide Styling Section */}
            <div style={{ marginTop: '20px', marginBottom: '20px', borderTop: '1px solid #dfe3e8', paddingTop: '20px' }}>
              <Text variant="headingMd" as="h3">Fit Guide Styling</Text>
              <Text variant="bodyMd" as="p" color="subdued">Customize the appearance of the Fit Guide section</Text>
              
              {/* Fit Guide Background Color */}
              <div style={{ marginTop: '16px' }}>
                <ColorPickerField
                  label="Fit Guide Background Color"
                  value={chartStyle.fitGuideBgColor}
                  onChange={(value) => setChartStyle({...chartStyle, fitGuideBgColor: value})}
                  helpText="Background color for the entire Fit Guide section"
                />
              </div>
              
              {/* Fit Guide Heading Color */}
              <div style={{ marginTop: '16px' }}>
                <ColorPickerField
                  label="Fit Guide Heading Color"
                  value={chartStyle.fitGuideHeadingColor}
                  onChange={(value) => setChartStyle({...chartStyle, fitGuideHeadingColor: value})}
                  helpText="Text color for the Fit Guide heading"
                />
              </div>
              
              {/* Fit Guide Text Color */}
              <div style={{ marginTop: '16px' }}>
                <ColorPickerField
                  label="Fit Guide Text Color"
                  value={chartStyle.fitGuideTextColor}
                  onChange={(value) => setChartStyle({...chartStyle, fitGuideTextColor: value})}
                  helpText="Text color for the Fit Guide descriptions"
                />
              </div>
              
              {/* Fit Guide Accent Color */}
              <div style={{ marginTop: '16px' }}>
                <ColorPickerField
                  label="Fit Guide Accent Color"
                  value={chartStyle.fitGuideAccentColor}
                  onChange={(value) => setChartStyle({...chartStyle, fitGuideAccentColor: value})}
                  helpText="Accent color for borders and highlights"
                />
              </div>
              
              {/* Fit Guide Visualizer Dot Color */}
              <div style={{ marginTop: '16px' }}>
                <ColorPickerField
                  label="Fit Guide Dot Color"
                  value={chartStyle.fitGuideDotColor}
                  onChange={(value) => setChartStyle({...chartStyle, fitGuideDotColor: value})}
                  helpText="Color of the indicator dots in the fit guide visualizer"
                />
              </div>
              
              {/* Fit Guide Visualizer Track Color */}
              <div style={{ marginTop: '16px' }}>
                <ColorPickerField
                  label="Fit Guide Track Color"
                  value={chartStyle.fitGuideTrackColor}
                  onChange={(value) => setChartStyle({...chartStyle, fitGuideTrackColor: value})}
                  helpText="Color of the track/bar in the fit guide visualizer"
                />
              </div>
              
              {/* Fit Guide Visualizer Label Color */}
              <div style={{ marginTop: '16px' }}>
                <ColorPickerField
                  label="Fit Guide Label Color"
                  value={chartStyle.fitGuideLabelColor}
                  onChange={(value) => setChartStyle({...chartStyle, fitGuideLabelColor: value})}
                  helpText="Color of the labels in the fit guide visualizer"
                />
              </div>
              
              {/* Fit Guide Card Background Color */}
              <div style={{ marginTop: '16px' }}>
                <ColorPickerField
                  label="Size Card Background Color"
                  value={chartStyle.fitGuideCardBgColor}
                  onChange={(value) => setChartStyle({...chartStyle, fitGuideCardBgColor: value})}
                  helpText="Background color for individual size cards"
                />
              </div>
              
              {/* Fit Tag Colors */}
              <div style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
                {/* Fit Tag Background Color */}
                <div style={{ flex: 1 }}>
                  <ColorPickerField
                    label="Fit Tag Background"
                    value={chartStyle.fitGuideTagBgColor}
                    onChange={(value) => setChartStyle({...chartStyle, fitGuideTagBgColor: value})}
                    helpText="Background for fit tags"
                  />
                </div>
                
                {/* Fit Tag Text Color */}
                <div style={{ flex: 1 }}>
                  <ColorPickerField
                    label="Fit Tag Text"
                    value={chartStyle.fitGuideTagTextColor}
                    onChange={(value) => setChartStyle({...chartStyle, fitGuideTagTextColor: value})}
                    helpText="Text color for fit tags"
                  />
                </div>
              </div>
            </div>
          </div>
      </FormLayout>
      </Card.Section>
      
      {/* Preview Section - Moved to its own Card.Section for clear separation */}
      <Card.Section title="Preview">
        <div style={{ padding: '16px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#ffffff' }}>
          <Text variant="headingMd">Style Preview</Text>
          
          {/* Chart Title Preview */}
          {headerText && (
            <div style={{ marginTop: '16px', marginBottom: '16px', fontFamily: chartStyle.fontFamily }}>
              <Text variant="headingMd" style={{ color: chartStyle.chartTitleColor, fontFamily: chartStyle.fontFamily }}>{headerText}</Text>
            </div>
          )}
          
          {/* Template Preview */}
          <div style={{ marginTop: '20px' }}>
            <Text variant="headingSm">Template Preview:</Text>
            
            <div style={{ 
              marginTop: '16px',
              padding: '16px', 
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              backgroundColor: 'white',
              fontFamily: chartStyle.fontFamily
            }}>
              {/* Fit & Stretch Guide Visualizer (First in preview) */}
              <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: chartStyle.fitGuideBgColor || '#f4f6f8', borderRadius: '4px' }}>
                <FitGuideVisualizer 
                  fitType='regular' 
                  stretchLevel='moderate' 
                  fontFamily={chartStyle.fontFamily}
                  showLabels={true}
                  dotColor={chartStyle.fitGuideDotColor}
                  trackColor={chartStyle.fitGuideTrackColor}
                  labelColor={chartStyle.fitGuideLabelColor}
                  indicatorShape={chartStyle.fitGuideIndicatorShape}
                  indicatorSize={chartStyle.fitGuideIndicatorSize}
                  indicatorOffset={chartStyle.fitGuideIndicatorOffset}
                />
              </div>
              
              {/* Size Chart Table (Second in preview) */}
              <div style={{ marginBottom: '24px' }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse',
                  fontFamily: chartStyle.fontFamily
                }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '10px', borderBottom: `1px solid ${chartStyle.borderColor}`, backgroundColor: chartStyle.headerBgColor, color: chartStyle.headerTextColor, textAlign: 'left' }}>Size</th>
                      <th style={{ padding: '10px', borderBottom: `1px solid ${chartStyle.borderColor}`, backgroundColor: chartStyle.headerBgColor, color: chartStyle.headerTextColor, textAlign: 'left' }}>Bust (in)</th>
                      <th style={{ padding: '10px', borderBottom: `1px solid ${chartStyle.borderColor}`, backgroundColor: chartStyle.headerBgColor, color: chartStyle.headerTextColor, textAlign: 'left' }}>Waist (in)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '10px', borderBottom: `1px solid ${chartStyle.borderColor}`, fontWeight: 'bold', color: chartStyle.cellTextColor }}>S</td>
                      <td style={{ padding: '10px', borderBottom: `1px solid ${chartStyle.borderColor}`, color: chartStyle.cellTextColor }}>34-36</td>
                      <td style={{ padding: '10px', borderBottom: `1px solid ${chartStyle.borderColor}`, color: chartStyle.cellTextColor }}>28-30</td>
                    </tr>
                    <tr style={{ backgroundColor: chartStyle.rowAlternateColor }}>
                      <td style={{ padding: '10px', borderBottom: `1px solid ${chartStyle.borderColor}`, fontWeight: 'bold', color: chartStyle.alternateRowTextColor }}>M</td>
                      <td style={{ padding: '10px', borderBottom: `1px solid ${chartStyle.borderColor}`, color: chartStyle.alternateRowTextColor }}>36-38</td>
                      <td style={{ padding: '10px', borderBottom: `1px solid ${chartStyle.borderColor}`, color: chartStyle.alternateRowTextColor }}>30-32</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              {/* How to Measure (Third in preview) */}
              <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: chartStyle.measurementBgColor || '#f4f6f8', borderRadius: '4px' }}>
                <Text variant="headingMd" style={{ color: chartStyle.measurementHeadingColor, fontFamily: chartStyle.fontFamily, marginBottom: '12px' }}>
                  How to Measure the Product's Size?
                </Text>
                <MeasurementDiagram 
                  garmentType='dress' 
                  fontFamily={chartStyle.fontFamily}
                  backgroundColor={chartStyle.measurementBgColor || '#f4f6f8'}
                  headingColor={chartStyle.measurementHeadingColor || '#212b36'}
                  textColor={chartStyle.measurementTextColor || '#637381'}
                  numberBgColor={chartStyle.measurementNumberBgColor || '#0068d9'}
                  numberColor={chartStyle.measurementNumberColor || '#ffffff'}
                />
              </div>
              
              {/* Size-Specific Fit Guide (Fourth in preview) */}
              <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: chartStyle.fitGuideBgColor || '#f4f6f8', borderRadius: '4px' }}>
                <Text variant="headingMd" style={{ color: chartStyle.fitGuideHeadingColor, fontFamily: chartStyle.fontFamily, marginBottom: '12px' }}>Fit Guide</Text>
                <div style={{ 
                  backgroundColor: chartStyle.fitGuideCardBgColor || 'white', 
                  borderLeft: `4px solid ${chartStyle.fitGuideAccentColor || '#0068d9'}`,
                  padding: '12px',
                  borderRadius: '0 4px 4px 0'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text variant="headingSm" style={{ color: chartStyle.fitGuideHeadingColor, fontFamily: chartStyle.fontFamily }}>S (Size 6)</Text>
                    <span style={{ 
                      backgroundColor: chartStyle.fitGuideTagBgColor || '#f2f7fe', 
                      color: chartStyle.fitGuideTagTextColor || '#0068d9', 
                      padding: '2px 8px', 
                      borderRadius: '12px', 
                      fontSize: '13px'
                    }}>
                      Regular Fit
                    </span>
                  </div>
                  <Text variant="bodyMd" style={{ marginTop: '8px', color: chartStyle.fitGuideTextColor, fontFamily: chartStyle.fontFamily }}>True to size, offers a comfortable fit</Text>
                  <Text variant="bodyMd" style={{ marginTop: '4px', fontStyle: 'italic', color: chartStyle.fitGuideTextColor, fontFamily: chartStyle.fontFamily }}>Best for bust measurements between 34-36 inches</Text>
                </div>
              </div>
              
              {/* Notes Section (Fifth in preview) */}
              <div style={{ marginBottom: '24px', backgroundColor: chartStyle.notesBgColor || '#f9fafb', padding: '12px', borderRadius: '4px' }}>
                <Text variant="headingMd" style={{ color: chartStyle.notesHeadingColor || chartStyle.fitGuideHeadingColor, fontFamily: chartStyle.fontFamily, marginBottom: '8px' }}>Notes:</Text>
                <ul style={{ marginTop: '8px', paddingLeft: '20px', fontFamily: chartStyle.fontFamily }}>
                  <li style={{ marginBottom: '4px', color: chartStyle.notesTextColor || chartStyle.fitGuideTextColor }}>Measurements may vary by 1-2 inches</li>
                  <li style={{ marginBottom: '4px', color: chartStyle.notesTextColor || chartStyle.fitGuideTextColor }}>Please refer to the size guide before ordering</li>
                </ul>
              </div>
            </div>
          </div>
          
          {/* Footer Text Preview */}
          {footerText && (
            <div style={{ marginTop: '16px', marginBottom: '16px' }}>
              <Text variant="bodyMd" style={{ color: chartStyle.footerTextColor, fontFamily: chartStyle.fontFamily }}>{footerText}</Text>
            </div>
          )}
          
          {/* Info Text Preview */}
          {infoText && (
            <div style={{ marginTop: '16px', padding: '12px', backgroundColor: chartStyle.infoBgColor || '#f9fafb', borderRadius: '4px' }}>
              <Text variant="bodyMd" style={{ color: chartStyle.infoTextColor, fontFamily: chartStyle.fontFamily }}>{infoText}</Text>
            </div>
          )}
        </div>
      </Card.Section>
        
        <Card.Section>
          <Banner status="info">
            <p>This template will be applied to size charts generated with the Product Chart Generator.</p>
          </Banner>
        </Card.Section>
      </Card>
    );
  };
  
  // Render generated charts tab
  const renderGeneratedCharts = () => {
    // Debug the generated charts data
    console.log('Generated charts:', generatedCharts);
    
    return (
      <Card>
        <Card.Section>
          <Text variant="headingMd">Generated Size Charts</Text>
          <Text variant="bodyMd">View, edit, and manage your generated size charts.</Text>
        </Card.Section>
        
        {isLoading ? (
          <Card.Section>
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spinner size="large" />
              <div style={{ marginTop: '16px' }}>
                <Text variant="bodyMd">Loading charts...</Text>
              </div>
            </div>
          </Card.Section>
        ) : generatedCharts.length === 0 ? (
          <Card.Section>
            <EmptyState
              heading="No charts generated yet"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>Generate your first size chart using the Product Chart Generator.</p>
            </EmptyState>
          </Card.Section>
        ) : (
          generatedCharts.map((chart) => (
            <Card.Section key={chart.id}>
              <LegacyStack distribution="equalSpacing">
                <div style={{ marginBottom: '16px' }}>
                  {/* Handle the nested structure - the actual sizechart data might be in chart.sizechart.sizechart */}
                  {(() => {
                    const chartData = chart.sizechart?.sizechart || chart.sizechart;
                    const chartSummary = chart.sizechart?.summary || chart.summary || '';
                    const chartCountry = chart.sizechart?.country || '';
                    
                    return (
                      <>
                        <Text variant="headingMd">{chartData?.name || 'Untitled Chart'}</Text>
                        <Text variant="bodyMd" color="subdued">
                          {chartData?.category || 'No category'} • 
                          {chartCountry ? `Country: ${chartCountry} • ` : ''}
                          {chart.createdAt ? new Date(chart.createdAt.seconds * 1000).toLocaleDateString() : 'Unknown date'}
                        </Text>
                        {chartSummary && <Text variant="bodyMd" color="subdued">{chartSummary}</Text>}
                      </>
                    );
                  })()} 
                </div>
                <div>
                  <LegacyStack>
                    <Button onClick={() => handlePreviewChart(chart)} icon={ViewMinor}>Preview</Button>
                    <Button onClick={() => exportChartAsImage(chart)} icon={ExportMinor}>Export</Button>
                    <Button onClick={() => uploadChartToShopify(chart)} primary>Upload to Shopify</Button>
                    <Button onClick={() => deleteChart(chart.id)} destructive icon={DeleteMinor}>Delete</Button>
                  </LegacyStack>
                </div>
              </LegacyStack>
              
              <div style={{ marginTop: '16px', overflowX: 'auto', fontFamily: chartStyle.fontFamily }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse',
                  fontSize: chartStyle.fontSize
                }}>
                  <thead>
                    <tr>
                      {chart.sizechart?.columns?.map((col, index) => (
                        <th key={index} style={{ 
                          padding: '10px', 
                          borderBottom: `1px solid ${chartStyle.borderColor}`,
                          backgroundColor: chartStyle.headerBgColor,
                          textAlign: 'left' 
                        }}>
                          {col.unit === 'none' ? col.name : `${col.name} (${col.unit})`}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {chart.sizechart?.rows?.map((row, rowIndex) => {
                      // Check if we're using the new format with original_size and country_size
                      const isNewFormat = row.hasOwnProperty('original_size') && row.hasOwnProperty('country_size');
                      
                      return (
                        <tr key={rowIndex} style={{
                          backgroundColor: rowIndex % 2 === 1 ? chartStyle.rowAlternateColor : 'transparent'
                        }}>
                          {/* Handle both old and new format */}
                          {isNewFormat ? (
                            // New format with original_size and country_size
                            <>
                              <td style={{ 
                                padding: '10px', 
                                borderBottom: `1px solid ${chartStyle.borderColor}`, 
                                fontWeight: 'bold' 
                              }}>
                                {row.original_size || '-'}
                              </td>
                              <td style={{ 
                                padding: '10px', 
                                borderBottom: `1px solid ${chartStyle.borderColor}` 
                              }}>
                                {row.country_size || '-'}
                              </td>
                              {row.values?.map((value, valueIndex) => (
                                <td key={valueIndex} style={{ 
                                  padding: '10px', 
                                  borderBottom: `1px solid ${chartStyle.borderColor}`
                                }}>
                                  {value || '-'}
                                </td>
                              ))}
                            </>
                          ) : (
                            // Old format with just size
                            <>
                              <td style={{ 
                                padding: '10px', 
                                borderBottom: `1px solid ${chartStyle.borderColor}`, 
                                fontWeight: 'bold' 
                              }}>
                                {row.size || '-'}
                              </td>
                              {row.values?.map((value, valueIndex) => (
                                <td key={valueIndex} style={{ 
                                  padding: '10px', 
                                  borderBottom: `1px solid ${chartStyle.borderColor}`
                                }}>
                                  {value || '-'}
                                </td>
                              ))}
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card.Section>
          ))
        )}
      </Card>
    );
  };
  
  // State for chart editing
  const [isEditMode, setIsEditMode] = useState(false);
  const [editableChart, setEditableChart] = useState(null);
  const [originalChart, setOriginalChart] = useState(null);
  
  // State for confirmation dialog
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  
  // Function to enter edit mode
  const handleEditChart = () => {
    // Create a deep copy of the chart for editing
    setOriginalChart(JSON.parse(JSON.stringify(previewChart)));
    setEditableChart(JSON.parse(JSON.stringify(previewChart)));
    setIsEditMode(true);
    
    // If the chart has a templateId, select that template
    if (previewChart.templateId) {
      const template = templates.find(t => t.id === previewChart.templateId);
      if (template) {
        setSelectedTemplate(template);
      }
    }
  };
  
  // Function to save edited chart
  const handleSaveChart = async () => {
    try {
      setIsLoading(true);
      
      if (editableChart && editableChart.id) {
        const chartRef = doc(db, `shops/${shopId}/generated_sizecharts`, editableChart.id);
        
        // Create update data object with all fields that need to be updated
        const updateData = {
          sizechart: editableChart.sizechart,
          language: selectedLanguage, // Save the selected language
          lastUpdated: serverTimestamp(),
          templateId: selectedTemplate?.id // Save the selected template ID
        };
        
        // Add garment template information if available
        if (editableChart.garmentType) updateData.garmentType = editableChart.garmentType;
        if (editableChart.fitType) updateData.fitType = editableChart.fitType;
        if (editableChart.stretchLevel) updateData.stretchLevel = editableChart.stretchLevel;
        if (editableChart.materialInfo) updateData.materialInfo = editableChart.materialInfo;
        if (editableChart.showMeasurementDiagram !== undefined) {
          updateData.showMeasurementDiagram = editableChart.showMeasurementDiagram;
        }
        
        // Save the chartStyle with the generated chart to ensure consistent styling
        // This includes all color values and the fit guide visualizer properties
        updateData.chartStyle = chartStyle;
        
        await updateDoc(chartRef, updateData);
        
        // Update the preview chart with the edited version
        setPreviewChart(editableChart);
        
        // Exit edit mode
        setIsEditMode(false);
        
        // Reload charts to show the updated data
        await loadGeneratedCharts();
        
        alert('Chart saved successfully!');
      }
    } catch (error) {
      console.error('Error saving chart:', error);
      alert(`Failed to save chart: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Function to restore original chart
  const handleRestoreChart = () => {
    if (originalChart) {
      setEditableChart(JSON.parse(JSON.stringify(originalChart)));
    }
  };
  
  // Helper function to determine the fit type from fit_info array
  const getFitTypeFromFitInfo = (fitInfo) => {
    if (!fitInfo || !Array.isArray(fitInfo) || fitInfo.length === 0) return null;
    
    // Count occurrences of each fit tag
    const fitTagCounts = {};
    fitInfo.forEach(item => {
      if (item.fit_tag) {
        const tag = item.fit_tag.toLowerCase();
        fitTagCounts[tag] = (fitTagCounts[tag] || 0) + 1;
      }
    });
    
    // Find the most common fit tag
    let mostCommonTag = null;
    let highestCount = 0;
    
    Object.entries(fitTagCounts).forEach(([tag, count]) => {
      if (count > highestCount) {
        mostCommonTag = tag;
        highestCount = count;
      }
    });
    
    // Map the fit tag to a fit type
    if (mostCommonTag) {
      if (mostCommonTag.includes('slim') || mostCommonTag.includes('fitted') || mostCommonTag.includes('tight')) {
        return 'slim';
      } else if (mostCommonTag.includes('loose') || mostCommonTag.includes('relaxed') || mostCommonTag.includes('oversized')) {
        return 'loose';
      } else if (mostCommonTag.includes('regular')) {
        return 'regular';
      }
    }
    
    return 'regular'; // Default to regular if no clear pattern
  };
  
  // Render chart preview modal
  const renderChartPreviewModal = () => {
    if (!previewChart) return null;
    
    // Use the editable chart when in edit mode, otherwise use the preview chart
    const displayChart = isEditMode ? editableChart : previewChart;
    
    // Use the chart's own chartStyle if available, otherwise use the selected template's chartStyle or the default chartStyle
    // This ensures consistent styling across all previews
    const displayChartStyle = isEditMode && selectedTemplate?.chartStyle ? 
      selectedTemplate.chartStyle : 
      (displayChart.chartStyle || chartStyle);
    
    // Debug the preview chart data
    console.log('Rendering preview for chart:', displayChart);
    console.log('Chart data:', displayChart?.sizechart);
    console.log('Chart style:', displayChartStyle);
    console.log('Fit type:', displayChart.fitType);
    console.log('Stretch level:', displayChart.stretchLevel);
    
    // Handle the nested structure - the actual sizechart data might be in displayChart.sizechart.sizechart
    const chartData = displayChart.sizechart?.sizechart || displayChart.sizechart;
    const chartSummary = displayChart.sizechart?.summary || displayChart.summary || '';
    const chartUnit = displayChart.sizechart?.unit || displayChart.unit || '';
    const chartCountry = displayChart.sizechart?.country || '';
    
    console.log('Extracted chart data:', chartData);
    console.log('Summary:', chartSummary);
    console.log('Unit:', chartUnit);
    console.log('Country:', chartCountry);
    
    return (
      <Modal
        open={isChartPreviewModalOpen}
        onClose={() => setIsChartPreviewModalOpen(false)}
        title={chartData?.name || 'Chart Preview'}
        large
      >
        <Modal.Section>
          <div style={{ marginBottom: '20px' }}>
            {/* Chart Info */}
            <div style={{ marginBottom: '16px' }}>
              {isEditMode ? (
                <FormLayout>
                  <TextField
                    label="Chart Name"
                    value={chartData?.name || ''}
                    onChange={(value) => {
                      const updatedChart = {...editableChart};
                      if (updatedChart.sizechart.sizechart) {
                        updatedChart.sizechart.sizechart.name = value;
                      } else {
                        updatedChart.sizechart.name = value;
                      }
                      setEditableChart(updatedChart);
                    }}
                  />
                  <FormLayout.Group>
                    <TextField
                      label="Category"
                      value={chartData?.category || ''}
                      onChange={(value) => {
                        const updatedChart = {...editableChart};
                        if (updatedChart.sizechart.sizechart) {
                          updatedChart.sizechart.sizechart.category = value;
                        } else {
                          updatedChart.sizechart.category = value;
                        }
                        setEditableChart(updatedChart);
                      }}
                    />
                    <TextField
                      label="Country"
                      value={chartCountry || ''}
                      onChange={(value) => {
                        const updatedChart = {...editableChart};
                        if (updatedChart.sizechart.sizechart) {
                          updatedChart.sizechart.sizechart.country = value;
                        } else {
                          updatedChart.sizechart.country = value;
                        }
                        setEditableChart(updatedChart);
                      }}
                    />
                  </FormLayout.Group>
                  
                  <FormLayout.Group>
                    <Select
                      label="Display Language"
                      options={languageOptions}
                      value={selectedLanguage}
                      onChange={(value) => {
                        setSelectedLanguage(value);
                        // Update translated measurement points and garment types
                        setTranslatedMeasurementPoints(translateMeasurementPoints(measurementPoints, value));
                        setTranslatedGarmentTypes(translateGarmentTypes(garmentTypes, value));
                      }}
                      helpText="Select the language for size chart elements like titles and labels"
                    />
                    <Select
                      label="Template"
                      options={templates.map(template => ({
                        label: template.name,
                        value: template.id
                      }))}
                      value={selectedTemplate?.id || ''}
                      onChange={(value) => {
                        const template = templates.find(t => t.id === value);
                        if (template) {
                          // Load the template settings
                          loadTemplateSettings(template);
                          
                          // Apply the template's style to the editable chart
                          const updatedChart = {...editableChart};
                          updatedChart.templateId = template.id;
                          
                          // Apply the template's chartStyle
                          if (template.chartStyle) {
                            updatedChart.chartStyle = {...template.chartStyle};
                          }
                          
                          // Apply other template settings if available
                          if (template.headerText) updatedChart.headerText = template.headerText;
                          if (template.footerText) updatedChart.footerText = template.footerText;
                          if (template.infoText) updatedChart.infoText = template.infoText;
                          if (template.showHeaderImage !== undefined) updatedChart.showHeaderImage = template.showHeaderImage;
                          if (template.headerImageUrl) updatedChart.headerImageUrl = template.headerImageUrl;
                          if (template.showFooterImage !== undefined) updatedChart.showFooterImage = template.showFooterImage;
                          if (template.footerImageUrl) updatedChart.footerImageUrl = template.footerImageUrl;
                          
                          setEditableChart(updatedChart);
                          setSelectedTemplate(template);
                        }
                      }}
                      helpText="Select a template to apply to this chart"
                    />
                  </FormLayout.Group>
                  <TextField
                    label="Summary"
                    value={chartSummary || ''}
                    multiline={3}
                    onChange={(value) => {
                      const updatedChart = {...editableChart};
                      if (updatedChart.sizechart.summary) {
                        updatedChart.sizechart.summary = value;
                      } else if (updatedChart.summary) {
                        updatedChart.summary = value;
                      } else {
                        updatedChart.sizechart.summary = value;
                      }
                      setEditableChart(updatedChart);
                    }}
                  />
                  
                  <FormLayout.Group>
                    <Select
                      label="Garment Type"
                      options={[
                        // Single garments
                        {label: 'Dress', value: 'dress'},
                        {label: 'Top/Shirt', value: 'top'},
                        {label: 'Pants/Trousers', value: 'pants'},
                        {label: 'Skirt', value: 'skirt'},
                        {label: 'Jacket/Coat', value: 'jacket'},
                        {label: 'Shoes', value: 'shoes'},
                        {label: 'Accessories', value: 'accessories'},
                        
                        // New garment types
                        {label: 'Romper/Jumpsuit', value: 'romper'},
                        {label: 'Swimwear', value: 'swimwear'},
                        
                        // Underwear and lingerie
                        {label: "Men's Underwear", value: 'mens_underwear'},
                        {label: "Women's Underwear/Panties", value: 'womens_underwear'},
                        {label: 'Bra', value: 'bra'},
                        {label: 'Lingerie Set (Bra & Underwear)', value: 'lingerie_set'},
                        {label: 'Shapewear', value: 'shapewear'},
                        
                        // Sets and combinations
                        {label: '2-Piece Set (Top & Bottom)', value: 'two_piece_set'},
                        {label: '2-Piece Set (Top & Skirt)', value: 'two_piece_skirt_set'},
                        {label: '3-Piece Set (Top, Bottom & Jacket)', value: 'three_piece_set'},
                        {label: 'Suit (Jacket & Pants)', value: 'suit'},
                        {label: 'Tracksuit', value: 'tracksuit'}
                      ]}
                      value={editableChart.garmentType || 'dress'}
                      onChange={(value) => {
                        const updatedChart = {...editableChart};
                        updatedChart.garmentType = value;
                        setEditableChart(updatedChart);
                      }}
                    />
                    
                    <Select
                      label="Fit Type"
                      options={[
                        {label: 'Slim', value: 'slim'},
                        {label: 'Regular', value: 'regular'},
                        {label: 'Relaxed', value: 'relaxed'},
                        {label: 'Oversized', value: 'oversized'}
                      ]}
                      value={editableChart.fitType || 'regular'}
                      onChange={(value) => {
                        const updatedChart = {...editableChart};
                        updatedChart.fitType = value;
                        setEditableChart(updatedChart);
                      }}
                    />
                    
                    <Select
                      label="Stretch Level"
                      options={[
                        {label: 'None', value: 'none'},
                        {label: 'Slight', value: 'slight'},
                        {label: 'Moderate', value: 'moderate'},
                        {label: 'High', value: 'high'}
                      ]}
                      value={editableChart.stretchLevel || 'none'}
                      onChange={(value) => {
                        const updatedChart = {...editableChart};
                        updatedChart.stretchLevel = value;
                        setEditableChart(updatedChart);
                      }}
                    />
                  </FormLayout.Group>
                  
                  <TextField
                    label="Material Information"
                    value={editableChart.materialInfo || ''}
                    onChange={(value) => {
                      const updatedChart = {...editableChart};
                      updatedChart.materialInfo = value;
                      setEditableChart(updatedChart);
                    }}
                    helpText="Enter fabric composition (e.g., '95% Cotton, 5% Elastane')"
                  />
                  
                  <Checkbox
                    label="Show Measurement Diagram"
                    checked={editableChart.showMeasurementDiagram !== false}
                    onChange={(checked) => {
                      const updatedChart = {...editableChart};
                      updatedChart.showMeasurementDiagram = checked;
                      setEditableChart(updatedChart);
                    }}
                  />
                </FormLayout>
              ) : (
                <>
                  <Text variant="headingLg">{chartData?.name || 'Size Chart'}</Text>
                  <Text variant="bodyMd" color="subdued">
                    Category: {chartData?.category || 'No category'} • 
                    Country: {chartCountry || 'Not specified'}
                  </Text>
                  <Text variant="bodyMd" color="subdued">
                    {chartSummary}
                  </Text>
                </>
              )}
            </div>
            
            {/* Header Text */}
            {headerText && (
              <div style={{ marginBottom: '16px', fontFamily: chartStyle.fontFamily }}>
                <Text variant="headingMd" style={{ color: chartStyle.chartTitleColor, fontFamily: chartStyle.fontFamily }}>{headerText}</Text>
              </div>
            )}
            
            {/* Header Image */}
            {showHeaderImage && headerImageUrl && (
              <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                <img 
                  src={headerImageUrl} 
                  alt="Header" 
                  style={{ maxWidth: '100%', height: 'auto' }}
                />
              </div>
            )}
            
            {/* Fit & Stretch Guide Visualizer - Always shown above size chart table */}
            {!isEditMode && (
              <div style={{ marginBottom: '16px', padding: '16px', backgroundColor: displayChartStyle.fitGuideBgColor || '#f4f6f8', borderRadius: '4px' }}>
                {/* Removed the heading to avoid duplication with the FitGuideVisualizer component */}
                {console.log('About to render FitGuideVisualizer with:', {
                  fitType: displayChart.fitType || displayChart.sizechart?.fit_type || displayChart.sizechart?.sizechart?.fit_type || 'regular',
                  stretchLevel: displayChart.stretchLevel || displayChart.sizechart?.stretch_level || displayChart.sizechart?.sizechart?.stretch_level || 'none'
                })}
                <FitGuideVisualizer
                  fitType={displayChart.fitType || displayChart.sizechart?.fit_type || displayChart.sizechart?.sizechart?.fit_type || 'regular'}
                  stretchLevel={displayChart.stretchLevel || displayChart.sizechart?.stretch_level || displayChart.sizechart?.sizechart?.stretch_level || 'none'}
                  accentColor={displayChartStyle.fitGuideAccentColor || '#0068d9'}
                  backgroundColor={displayChartStyle.fitGuideBgColor || '#f4f6f8'}
                  dotColor={displayChartStyle.fitGuideDotColor || '#000000'}
                  trackColor={displayChartStyle.fitGuideTrackColor || '#e6e6e6'}
                  labelColor={displayChartStyle.fitGuideLabelColor || '#637381'}
                  indicatorShape={displayChartStyle.fitGuideIndicatorShape || 'dot'}
                  indicatorSize={displayChartStyle.fitGuideIndicatorSize || 16}
                  indicatorOffset={displayChartStyle.fitGuideIndicatorOffset || 0}
                  language={displayChart.language || 'en'}
                  translations={defaultTranslations.elements[displayChart.language || 'en']}
                />
              </div>
            )}
            
            {/* Size Chart Table */}
            <div style={{ marginBottom: '16px', border: `1px solid ${displayChartStyle.borderColor || '#ddd'}`, borderRadius: '4px', padding: '8px' }}>
              {isEditMode ? (
                <div>
                  <LegacyStack distribution="equalSpacing" alignment="center" spacing="tight" wrap={false}>
                    <Text variant="headingSm">Table Columns</Text>
                    <Button size="slim" onClick={() => {
                      const updatedChart = JSON.parse(JSON.stringify(editableChart));
                      const chartDataRef = updatedChart.sizechart.sizechart || updatedChart.sizechart;
                      
                      // Add a new column
                      const newColumn = {
                        name: `Column ${chartDataRef.columns.length + 1}`,
                        key: `column_${chartDataRef.columns.length + 1}`,
                        unit: 'in'
                      };
                      
                      chartDataRef.columns.push(newColumn);
                      
                      // Add empty value to each row for this column
                      chartDataRef.rows.forEach(row => {
                        if (row.values) {
                          row.values.push('');
                        } else {
                          row.values = [''];
                        }
                      });
                      
                      setEditableChart(updatedChart);
                    }}>Add Column</Button>
                  </LegacyStack>
                  
                  <div style={{ overflowX: 'auto', marginTop: '8px' }}>
                    <table style={{ 
                      width: '100%', 
                      borderCollapse: 'collapse',
                      fontFamily: displayChartStyle.fontFamily,
                      fontSize: displayChartStyle.fontSize
                    }}>
                      <thead>
                        <tr>
                          {chartData?.columns?.map((col, colIndex) => (
                            <th key={colIndex} style={{ 
                              padding: '10px', 
                              borderBottom: `1px solid ${displayChartStyle.borderColor}`,
                              backgroundColor: displayChartStyle.headerBgColor,
                              textAlign: 'left' 
                            }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <TextField
                                  label="Column Name"
                                  labelHidden
                                  value={col.name || ''}
                                  onChange={(value) => {
                                    const updatedChart = JSON.parse(JSON.stringify(editableChart));
                                    const chartDataRef = updatedChart.sizechart.sizechart || updatedChart.sizechart;
                                    chartDataRef.columns[colIndex].name = value;
                                    setEditableChart(updatedChart);
                                  }}
                                />
                                {colIndex > 0 && (
                                  <Select
                                    label="Unit"
                                    labelHidden
                                    options={[
                                      {label: 'in (inches)', value: 'in'},
                                      {label: 'cm (centimeters)', value: 'cm'},
                                      {label: 'None', value: 'none'}
                                    ]}
                                    value={col.unit || 'none'}
                                    onChange={(value) => {
                                      const updatedChart = JSON.parse(JSON.stringify(editableChart));
                                      const chartDataRef = updatedChart.sizechart.sizechart || updatedChart.sizechart;
                                      chartDataRef.columns[colIndex].unit = value;
                                      setEditableChart(updatedChart);
                                    }}
                                  />
                                )}
                                <div style={{ display: 'flex', gap: '4px' }}>
                                  {colIndex > 0 && (
                                    <Button
                                      size="slim"
                                      icon={ArrowLeftMinor}
                                      onClick={() => {
                                        const updatedChart = JSON.parse(JSON.stringify(editableChart));
                                        const chartDataRef = updatedChart.sizechart.sizechart || updatedChart.sizechart;
                                        
                                        // Move column left
                                        const column = chartDataRef.columns[colIndex];
                                        chartDataRef.columns.splice(colIndex, 1);
                                        chartDataRef.columns.splice(colIndex - 1, 0, column);
                                        
                                        // Handle different column types
                                        if (colIndex === 1) {
                                          // Moving country_size to position 0 (original_size)
                                          chartDataRef.rows.forEach(row => {
                                            // Swap original_size and country_size values
                                            const temp = row.original_size;
                                            row.original_size = row.country_size;
                                            row.country_size = temp;
                                          });
                                        } else if (colIndex === 2) {
                                          // Moving first measurement column to position 1 (country_size)
                                          chartDataRef.rows.forEach(row => {
                                            // Store the first value from values array
                                            const firstValue = row.values[0];
                                            // Remove it from the values array
                                            row.values.splice(0, 1);
                                            // Store the current country_size
                                            const oldCountrySize = row.country_size;
                                            // Set country_size to the first value
                                            row.country_size = firstValue;
                                            // Add the old country_size as the first element in values
                                            row.values.unshift(oldCountrySize);
                                          });
                                        } else if (colIndex > 2) {
                                          // Moving a measurement column left
                                          const valueIndex = colIndex - 2; // Adjust for original_size and country_size
                                          chartDataRef.rows.forEach(row => {
                                            if (row.values && row.values.length > valueIndex) {
                                              const value = row.values[valueIndex];
                                              row.values.splice(valueIndex, 1);
                                              row.values.splice(valueIndex - 1, 0, value);
                                            }
                                          });
                                        }
                                        
                                        setEditableChart(updatedChart);
                                      }}
                                    />
                                  )}
                                  {colIndex < chartData.columns.length - 1 && (
                                    <Button
                                      size="slim"
                                      icon={ArrowRightMinor}
                                      onClick={() => {
                                        const updatedChart = JSON.parse(JSON.stringify(editableChart));
                                        const chartDataRef = updatedChart.sizechart.sizechart || updatedChart.sizechart;
                                        
                                        // Move column right
                                        const column = chartDataRef.columns[colIndex];
                                        chartDataRef.columns.splice(colIndex, 1);
                                        chartDataRef.columns.splice(colIndex + 1, 0, column);
                                        
                                        // Handle different column types
                                        if (colIndex === 0) {
                                          // Moving original_size to position 1 (country_size)
                                          chartDataRef.rows.forEach(row => {
                                            // Swap original_size and country_size values
                                            const temp = row.original_size;
                                            row.original_size = row.country_size;
                                            row.country_size = temp;
                                          });
                                        } else if (colIndex === 1) {
                                          // Moving country_size to first measurement column position
                                          chartDataRef.rows.forEach(row => {
                                            // Store the country_size
                                            const countrySize = row.country_size;
                                            // Store the first value from values array
                                            const firstValue = row.values[0];
                                            // Set country_size to the first value
                                            row.country_size = firstValue;
                                            // Replace the first value with the country_size
                                            row.values[0] = countrySize;
                                          });
                                        } else if (colIndex > 1) {
                                          // Moving a measurement column right
                                          const valueIndex = colIndex - 2; // Adjust for original_size and country_size
                                          chartDataRef.rows.forEach(row => {
                                            if (row.values && row.values.length > valueIndex + 1) {
                                              const value = row.values[valueIndex];
                                              row.values.splice(valueIndex, 1);
                                              row.values.splice(valueIndex + 1, 0, value);
                                            }
                                          });
                                        }
                                        
                                        setEditableChart(updatedChart);
                                      }}
                                    />
                                  )}
                                  {chartData.columns.length > 2 && (
                                    <Button
                                      size="slim"
                                      destructive
                                      onClick={() => {
                                        const updatedChart = JSON.parse(JSON.stringify(editableChart));
                                        const chartDataRef = updatedChart.sizechart.sizechart || updatedChart.sizechart;
                                        
                                        // Handle different column types for deletion
                                        if (colIndex === 0) {
                                          // Deleting original_size - move country_size to original_size position
                                          chartDataRef.columns.splice(colIndex, 1);
                                          chartDataRef.rows.forEach(row => {
                                            row.original_size = row.country_size;
                                            delete row.country_size;
                                            
                                            // If we have values, move the first one to country_size
                                            if (row.values && row.values.length > 0) {
                                              row.country_size = row.values[0];
                                              row.values.splice(0, 1);
                                            }
                                          });
                                        } else if (colIndex === 1) {
                                          // Deleting country_size - move first measurement to country_size position
                                          chartDataRef.columns.splice(colIndex, 1);
                                          chartDataRef.rows.forEach(row => {
                                            if (row.values && row.values.length > 0) {
                                              row.country_size = row.values[0];
                                              row.values.splice(0, 1);
                                            } else {
                                              delete row.country_size;
                                            }
                                          });
                                        } else {
                                          // Deleting a measurement column
                                          chartDataRef.columns.splice(colIndex, 1);
                                          
                                          // Remove the corresponding value from each row
                                          const valueIndex = colIndex - 2; // Adjust for original_size and country_size
                                          chartDataRef.rows.forEach(row => {
                                            if (row.values && row.values.length > valueIndex) {
                                              row.values.splice(valueIndex, 1);
                                            }
                                          });
                                        }
                                        
                                        setEditableChart(updatedChart);
                                      }}
                                    >
                                      Remove
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </th>
                          ))}
                          <th style={{ width: '80px', padding: '10px', textAlign: 'center' }}>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {chartData?.rows?.map((row, rowIndex) => {
                          // Check if we're using the new format with original_size and country_size
                          const isNewFormat = row.hasOwnProperty('original_size') && row.hasOwnProperty('country_size');
                          
                          return (
                            <tr key={rowIndex} style={{
                              backgroundColor: rowIndex % 2 === 1 ? chartStyle.rowAlternateColor : 'transparent'
                            }}>
                              {/* Handle both old and new format */}
                              {isNewFormat ? (
                                // New format with original_size and country_size
                                chartData.columns.map((column, colIndex) => {
                                  if (column.key === 'original_size') {
                                    return (
                                      <td key={colIndex} style={{ 
                                        padding: '10px', 
                                        borderBottom: `1px solid ${chartStyle.borderColor}`,
                                        color: rowIndex % 2 === 1 ? chartStyle.alternateRowTextColor : chartStyle.cellTextColor
                                      }}>
                                        <TextField
                                          value={row.original_size || ''}
                                          onChange={(value) => {
                                            const updatedChart = JSON.parse(JSON.stringify(editableChart));
                                            const chartDataRef = updatedChart.sizechart.sizechart || updatedChart.sizechart;
                                            chartDataRef.rows[rowIndex].original_size = value;
                                            setEditableChart(updatedChart);
                                          }}
                                        />
                                      </td>
                                    );
                                  } else if (column.key === 'country_size') {
                                    return (
                                      <td key={colIndex} style={{ 
                                        padding: '10px', 
                                        borderBottom: `1px solid ${chartStyle.borderColor}`,
                                        color: rowIndex % 2 === 1 ? chartStyle.alternateRowTextColor : chartStyle.cellTextColor
                                      }}>
                                        <TextField
                                          value={row.country_size || ''}
                                          onChange={(value) => {
                                            const updatedChart = JSON.parse(JSON.stringify(editableChart));
                                            const chartDataRef = updatedChart.sizechart.sizechart || updatedChart.sizechart;
                                            chartDataRef.rows[rowIndex].country_size = value;
                                            setEditableChart(updatedChart);
                                          }}
                                        />
                                      </td>
                                    );
                                  } else {
                                    // For measurement columns
                                    const valueIndex = colIndex - 2;
                                    return (
                                      <td key={colIndex} style={{ 
                                        padding: '10px', 
                                        borderBottom: `1px solid ${chartStyle.borderColor}`,
                                        color: rowIndex % 2 === 1 ? chartStyle.alternateRowTextColor : chartStyle.cellTextColor
                                      }}>
                                        <TextField
                                          value={valueIndex >= 0 && valueIndex < row.values.length ? row.values[valueIndex] : ''}
                                          onChange={(value) => {
                                            const updatedChart = JSON.parse(JSON.stringify(editableChart));
                                            const chartDataRef = updatedChart.sizechart.sizechart || updatedChart.sizechart;
                                            if (valueIndex >= 0) {
                                              if (!chartDataRef.rows[rowIndex].values) {
                                                chartDataRef.rows[rowIndex].values = [];
                                              }
                                              // Ensure the values array is long enough
                                              while (chartDataRef.rows[rowIndex].values.length <= valueIndex) {
                                                chartDataRef.rows[rowIndex].values.push('');
                                              }
                                              chartDataRef.rows[rowIndex].values[valueIndex] = value;
                                            }
                                            setEditableChart(updatedChart);
                                          }}
                                        />
                                      </td>
                                    );
                                  }
                                })
                              ) : (
                                // Old format with just size
                                chartData.columns.map((column, colIndex) => {
                                  if (colIndex === 0) {
                                    // First column is the size
                                    return (
                                      <td key={colIndex} style={{ 
                                        padding: '10px', 
                                        borderBottom: `1px solid ${chartStyle.borderColor}`,
                                        color: rowIndex % 2 === 1 ? chartStyle.alternateRowTextColor : chartStyle.cellTextColor
                                      }}>
                                        <TextField
                                          value={row.size || ''}
                                          onChange={(value) => {
                                            const updatedChart = JSON.parse(JSON.stringify(editableChart));
                                            const chartDataRef = updatedChart.sizechart.sizechart || updatedChart.sizechart;
                                            chartDataRef.rows[rowIndex].size = value;
                                            setEditableChart(updatedChart);
                                          }}
                                        />
                                      </td>
                                    );
                                  } else {
                                    // Other columns are values
                                    const valueIndex = colIndex - 1;
                                    return (
                                      <td key={colIndex} style={{ 
                                        padding: '10px', 
                                        borderBottom: `1px solid ${chartStyle.borderColor}`,
                                        color: rowIndex % 2 === 1 ? chartStyle.alternateRowTextColor : chartStyle.cellTextColor
                                      }}>
                                        <TextField
                                          value={valueIndex >= 0 && valueIndex < row.values.length ? row.values[valueIndex] : ''}
                                          onChange={(value) => {
                                            const updatedChart = JSON.parse(JSON.stringify(editableChart));
                                            const chartDataRef = updatedChart.sizechart.sizechart || updatedChart.sizechart;
                                            if (valueIndex >= 0) {
                                              if (!chartDataRef.rows[rowIndex].values) {
                                                chartDataRef.rows[rowIndex].values = [];
                                              }
                                              // Ensure the values array is long enough
                                              while (chartDataRef.rows[rowIndex].values.length <= valueIndex) {
                                                chartDataRef.rows[rowIndex].values.push('');
                                              }
                                              chartDataRef.rows[rowIndex].values[valueIndex] = value;
                                            }
                                            setEditableChart(updatedChart);
                                          }}
                                        />
                                      </td>
                                    );
                                  }
                                })
                              )}
                              <td style={{ padding: '10px', borderBottom: `1px solid ${chartStyle.borderColor}`, textAlign: 'center' }}>
                                <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                  {rowIndex > 0 && (
                                    <Button
                                      size="slim"
                                      icon={ArrowUpMinor}
                                      onClick={() => {
                                        const updatedChart = JSON.parse(JSON.stringify(editableChart));
                                        const chartDataRef = updatedChart.sizechart.sizechart || updatedChart.sizechart;
                                        
                                        // Move row up
                                        const row = chartDataRef.rows[rowIndex];
                                        chartDataRef.rows.splice(rowIndex, 1);
                                        chartDataRef.rows.splice(rowIndex - 1, 0, row);
                                        
                                        setEditableChart(updatedChart);
                                      }}
                                    />
                                  )}
                                  {rowIndex < chartData.rows.length - 1 && (
                                    <Button
                                      size="slim"
                                      icon={ArrowDownMinor}
                                      onClick={() => {
                                        const updatedChart = JSON.parse(JSON.stringify(editableChart));
                                        const chartDataRef = updatedChart.sizechart.sizechart || updatedChart.sizechart;
                                        
                                        // Move row down
                                        const row = chartDataRef.rows[rowIndex];
                                        chartDataRef.rows.splice(rowIndex, 1);
                                        chartDataRef.rows.splice(rowIndex + 1, 0, row);
                                        
                                        setEditableChart(updatedChart);
                                      }}
                                    />
                                  )}
                                  <Button
                                    size="slim"
                                    destructive
                                    onClick={() => {
                                      const updatedChart = JSON.parse(JSON.stringify(editableChart));
                                      const chartDataRef = updatedChart.sizechart.sizechart || updatedChart.sizechart;
                                      
                                      // Remove the row
                                      chartDataRef.rows.splice(rowIndex, 1);
                                      
                                      setEditableChart(updatedChart);
                                    }}
                                  >
                                    Delete
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  
                  <div style={{ marginTop: '8px' }}>
                    <Button
                      onClick={() => {
                        const updatedChart = JSON.parse(JSON.stringify(editableChart));
                        const chartDataRef = updatedChart.sizechart.sizechart || updatedChart.sizechart;
                        
                        // Create a new row based on the format (new or old)
                        const isNewFormat = chartDataRef.rows.length > 0 && 
                          chartDataRef.rows[0].hasOwnProperty('original_size') && 
                          chartDataRef.rows[0].hasOwnProperty('country_size');
                        
                        let newRow;
                        if (isNewFormat) {
                          newRow = {
                            original_size: '',
                            country_size: '',
                            values: []
                          };
                          
                          // Add empty values for each measurement column
                          const measurementColumnCount = chartDataRef.columns.length - 2; // Subtract original_size and country_size columns
                          for (let i = 0; i < measurementColumnCount; i++) {
                            newRow.values.push('');
                          }
                        } else {
                          newRow = {
                            size: '',
                            values: []
                          };
                          
                          // Add empty values for each measurement column
                          const measurementColumnCount = chartDataRef.columns.length - 1; // Subtract size column
                          for (let i = 0; i < measurementColumnCount; i++) {
                            newRow.values.push('');
                          }
                        }
                        
                        chartDataRef.rows.push(newRow);
                        setEditableChart(updatedChart);
                      }}
                    >
                      Add Row
                    </Button>
                  </div>
                </div>
              ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ 
                    width: '100%', 
                    borderCollapse: 'collapse',
                    fontFamily: displayChartStyle.fontFamily,
                    fontSize: displayChartStyle.fontSize
                  }}>
                    <thead>
                      <tr>
                        {chartData?.columns?.map((col, index) => (
                          <th key={index} style={{ 
                            padding: '10px', 
                            borderBottom: `1px solid ${displayChartStyle.borderColor}`,
                            backgroundColor: displayChartStyle.headerBgColor,
                            color: displayChartStyle.headerTextColor,
                            textAlign: 'left' 
                          }}>
                            {col.unit === 'none' ? col.name : `${col.name} (${col.unit})`}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {chartData?.rows?.map((row, rowIndex) => {
                        // Check if we're using the new format with original_size and country_size
                        const isNewFormat = row.hasOwnProperty('original_size') && row.hasOwnProperty('country_size');
                        
                        return (
                          <tr key={rowIndex} style={{
                            backgroundColor: rowIndex % 2 === 1 ? displayChartStyle.rowAlternateColor : 'transparent'
                          }}>
                            {/* Handle both old and new format */}
                            {isNewFormat ? (
                              // New format with original_size and country_size
                              // We need to handle each column separately to ensure correct alignment
                              chartData.columns.map((column, colIndex) => {
                                // Determine what to display based on the column key
                                if (column.key === 'original_size') {
                                  return (
                                    <td key={colIndex} style={{ 
                                      padding: '10px', 
                                      borderBottom: `1px solid ${chartStyle.borderColor}`, 
                                      fontWeight: 'bold',
                                      color: rowIndex % 2 === 1 ? chartStyle.alternateRowTextColor : chartStyle.cellTextColor
                                    }}>
                                      {row.original_size || '-'}
                                    </td>
                                  );
                                } else if (column.key === 'country_size') {
                                  return (
                                    <td key={colIndex} style={{ 
                                      padding: '10px', 
                                      borderBottom: `1px solid ${chartStyle.borderColor}`,
                                      color: rowIndex % 2 === 1 ? chartStyle.alternateRowTextColor : chartStyle.cellTextColor
                                    }}>
                                      {row.country_size || '-'}
                                    </td>
                                  );
                                } else {
                                  // For measurement columns (bust, waist, hip, etc.)
                                  // Find the index of this column in the values array
                                  // We need to subtract 2 from colIndex because the first two columns are original_size and country_size
                                  const valueIndex = colIndex - 2;
                                  return (
                                    <td key={colIndex} style={{ 
                                      padding: '10px', 
                                      borderBottom: `1px solid ${chartStyle.borderColor}`,
                                      color: rowIndex % 2 === 1 ? chartStyle.alternateRowTextColor : chartStyle.cellTextColor
                                    }}>
                                      {valueIndex >= 0 && valueIndex < row.values.length ? row.values[valueIndex] : '-'}
                                    </td>
                                  );
                                }
                              })
                            ) : (
                              // Old format with just size
                              // Map through all columns to ensure correct alignment
                              chartData.columns.map((column, colIndex) => {
                                if (colIndex === 0) {
                                  // First column is the size
                                  return (
                                    <td key={colIndex} style={{ 
                                      padding: '10px', 
                                      borderBottom: `1px solid ${chartStyle.borderColor}`, 
                                      fontWeight: 'bold',
                                      color: rowIndex % 2 === 1 ? chartStyle.alternateRowTextColor : chartStyle.cellTextColor
                                    }}>
                                      {row.size || '-'}
                                    </td>
                                  );
                                } else {
                                  // Other columns are values
                                  const valueIndex = colIndex - 1;
                                  return (
                                    <td key={colIndex} style={{ 
                                      padding: '10px', 
                                      borderBottom: `1px solid ${chartStyle.borderColor}`,
                                      color: rowIndex % 2 === 1 ? chartStyle.alternateRowTextColor : chartStyle.cellTextColor
                                    }}>
                                      {valueIndex >= 0 && valueIndex < row.values.length ? row.values[valueIndex] : '-'}
                                    </td>
                                  );
                                }
                              })
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            
            {/* Measurement Diagram */}
            {displayChart.garmentType && displayChart.showMeasurementDiagram && (
              <div style={{ marginBottom: '16px', padding: '16px', backgroundColor: displayChartStyle.fitGuideBgColor, borderRadius: '4px' }}>
                <Text variant="headingMd" style={{ color: displayChartStyle.fitGuideHeadingColor, fontFamily: displayChartStyle.fontFamily, marginBottom: '12px' }}>
                  {defaultTranslations.elements[displayChart.language || 'en'].howToMeasure || 'How to Measure the Product\'s Size?'}
                </Text>
                <MeasurementDiagram 
                  garmentType={displayChart.garmentType} 
                  fontFamily={displayChartStyle.fontFamily}
                  backgroundColor={displayChartStyle.measurementBgColor || '#f4f6f8'}
                  headingColor={displayChartStyle.measurementHeadingColor || '#212b36'}
                  textColor={displayChartStyle.measurementTextColor || '#637381'}
                  numberBgColor={displayChartStyle.measurementNumberBgColor || '#0068d9'}
                  numberColor={displayChartStyle.measurementNumberColor || '#ffffff'}
                  language={displayChart.language || 'en'}
                  translatedMeasurementPoints={translateMeasurementPoints(measurementPoints, displayChart.language || 'en')}
                />
              </div>
            )}
            
            {/* Fit Guide */}
            {chartData?.fit_info && chartData.fit_info.length > 0 && (
              <div style={{ marginBottom: '16px', fontFamily: displayChartStyle.fontFamily, fontSize: displayChartStyle.fontSize }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {/* Removed the heading to avoid duplication */}
                  {isEditMode && (
                    <Button
                      size="slim"
                      onClick={() => {
                        const updatedChart = JSON.parse(JSON.stringify(editableChart));
                        const chartDataRef = updatedChart.sizechart.sizechart || updatedChart.sizechart;
                        
                        // Add a new fit info item
                        if (!chartDataRef.fit_info) {
                          chartDataRef.fit_info = [];
                        }
                        
                        const newFitItem = {
                          original_size: '',
                          country_size: '',
                          fit_tag: 'Regular Fit',
                          fit_description: '',
                          buyer_hint: ''
                        };
                        
                        chartDataRef.fit_info.push(newFitItem);
                        setEditableChart(updatedChart);
                      }}
                    >
                      Add Fit Item
                    </Button>
                  )}
                </div>
                <div style={{ 
                  marginTop: '8px', 
                  backgroundColor: chartStyle.fitGuideBgColor, 
                  padding: '16px', 
                  borderRadius: '4px',
                  fontFamily: chartStyle.fontFamily
                }}>
                  {chartData.fit_info.map((fitItem, index) => {
                    // Determine the size to display
                    let sizeDisplay = '';
                    
                    // Check for original_size and country_size first (new format)
                    if (fitItem.original_size && fitItem.country_size) {
                      sizeDisplay = `${fitItem.original_size} (${fitItem.country_size})`;
                    } 
                    // Check for original_size and standard_size
                    else if (fitItem.original_size && fitItem.standard_size) {
                      sizeDisplay = `${fitItem.original_size} (${fitItem.standard_size})`;
                    }
                    // Fall back to just original_size if available
                    else if (fitItem.original_size) {
                      sizeDisplay = fitItem.original_size;
                    }
                    // Try country_size if original_size is not available
                    else if (fitItem.country_size) {
                      sizeDisplay = fitItem.country_size;
                    }
                    // Fall back to just size if available
                    else if (fitItem.size) {
                      sizeDisplay = fitItem.size;
                    }
                    // Last resort: use index
                    else {
                      sizeDisplay = `Size ${index + 1}`;
                    }
                    
                    return (
                      <div key={index} style={{ 
                        marginBottom: index < chartData.fit_info.length - 1 ? '16px' : '0',
                        backgroundColor: chartStyle.fitGuideCardBgColor,
                        borderLeft: `4px solid ${chartStyle.fitGuideAccentColor}`,
                        padding: '12px',
                        borderRadius: '0 4px 4px 0'
                      }}>
                        {isEditMode ? (
                          <div>
                            <LegacyStack distribution="equalSpacing" alignment="center">
                              <Text variant="headingSm">Fit Item {index + 1}</Text>
                              <Button
                                size="slim"
                                destructive
                                onClick={() => {
                                  const updatedChart = JSON.parse(JSON.stringify(editableChart));
                                  const chartDataRef = updatedChart.sizechart.sizechart || updatedChart.sizechart;
                                  
                                  // Remove this fit item
                                  chartDataRef.fit_info.splice(index, 1);
                                  setEditableChart(updatedChart);
                                }}
                              >
                                Remove
                              </Button>
                            </LegacyStack>
                            
                            <FormLayout>
                              <FormLayout.Group>
                                <TextField
                                  label="Size"
                                  value={fitItem.original_size || ''}
                                  onChange={(value) => {
                                    const updatedChart = JSON.parse(JSON.stringify(editableChart));
                                    const chartDataRef = updatedChart.sizechart.sizechart || updatedChart.sizechart;
                                    chartDataRef.fit_info[index].original_size = value;
                                    setEditableChart(updatedChart);
                                  }}
                                />
                                <TextField
                                  label="Country Size"
                                  value={fitItem.country_size || ''}
                                  onChange={(value) => {
                                    const updatedChart = JSON.parse(JSON.stringify(editableChart));
                                    const chartDataRef = updatedChart.sizechart.sizechart || updatedChart.sizechart;
                                    chartDataRef.fit_info[index].country_size = value;
                                    setEditableChart(updatedChart);
                                  }}
                                />
                              </FormLayout.Group>
                              
                              <TextField
                                label="Fit Tag"
                                value={fitItem.fit_tag || ''}
                                onChange={(value) => {
                                  const updatedChart = JSON.parse(JSON.stringify(editableChart));
                                  const chartDataRef = updatedChart.sizechart.sizechart || updatedChart.sizechart;
                                  chartDataRef.fit_info[index].fit_tag = value;
                                  setEditableChart(updatedChart);
                                }}
                              />
                              
                              <TextField
                                label="Fit Description"
                                value={fitItem.fit_description || ''}
                                multiline={2}
                                onChange={(value) => {
                                  const updatedChart = JSON.parse(JSON.stringify(editableChart));
                                  const chartDataRef = updatedChart.sizechart.sizechart || updatedChart.sizechart;
                                  chartDataRef.fit_info[index].fit_description = value;
                                  setEditableChart(updatedChart);
                                }}
                              />
                              
                              <TextField
                                label="Buyer Hint"
                                value={fitItem.buyer_hint || ''}
                                multiline={2}
                                onChange={(value) => {
                                  const updatedChart = JSON.parse(JSON.stringify(editableChart));
                                  const chartDataRef = updatedChart.sizechart.sizechart || updatedChart.sizechart;
                                  chartDataRef.fit_info[index].buyer_hint = value;
                                  setEditableChart(updatedChart);
                                }}
                              />
                            </FormLayout>
                          </div>
                        ) : (
                          <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Text variant="headingSm" style={{ color: chartStyle.fitGuideHeadingColor, fontFamily: chartStyle.fontFamily }}>{sizeDisplay}</Text>
                              {fitItem.fit_tag && (
                                <span style={{ 
                                  backgroundColor: chartStyle.fitGuideTagBgColor, 
                                  color: chartStyle.fitGuideTagTextColor, 
                                  padding: '2px 8px', 
                                  borderRadius: '12px', 
                                  fontSize: '13px',
                                  fontWeight: 'medium',
                                  fontFamily: chartStyle.fontFamily
                                }}>
                                  {fitItem.fit_tag}
                                </span>
                              )}
                            </div>
                            {fitItem.fit_description && (
                              <Text variant="bodyMd" style={{ marginTop: '8px', fontWeight: 'medium', color: chartStyle.fitGuideTextColor, fontFamily: chartStyle.fontFamily }}>{fitItem.fit_description}</Text>
                            )}
                            {fitItem.buyer_hint && (
                              <Text variant="bodyMd" style={{ marginTop: '6px', fontStyle: 'italic', color: chartStyle.fitGuideTextColor, fontFamily: chartStyle.fontFamily }}>{fitItem.buyer_hint}</Text>
                            )}
                            
                            {/* Body Measurement Recommendations */}
                            {fitItem.body_measurement_recommendation && Object.keys(fitItem.body_measurement_recommendation).length > 0 && (
                              <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {Object.entries(fitItem.body_measurement_recommendation).map(([key, value], idx) => {
                                  // Extract measurement name and unit from key (e.g., 'bust_in' -> 'Bust (in)')
                                  const [measurement, unit] = key.split('_');
                                  const displayName = measurement.charAt(0).toUpperCase() + measurement.slice(1);
                                  
                                  return (
                                    <div key={idx} style={{ backgroundColor: '#f9fafb', padding: '4px 10px', borderRadius: '16px', fontFamily: chartStyle.fontFamily }}>
                                      <Text variant="bodyMd" style={{ fontFamily: chartStyle.fontFamily }}>{displayName}: <strong>{value} {unit}</strong></Text>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            
                            {/* Measurement Instructions */}
                            {fitItem.measurement_instructions && (
                              <div style={{ marginTop: '8px' }}>
                                <Text variant="bodyMd" style={{ fontStyle: 'italic', color: chartStyle.fitGuideTextColor, fontFamily: chartStyle.fontFamily }}>{fitItem.measurement_instructions}</Text>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Notes */}
            {(isEditMode || (chartData?.notes && chartData.notes.length > 0)) && (
              <div style={{ marginBottom: '16px', backgroundColor: '#f9fafb', padding: '12px', borderRadius: '4px', fontFamily: chartStyle.fontFamily }}>
                {isEditMode ? (
                  <div>
                    <LegacyStack distribution="equalSpacing" alignment="center">
                      <TextField
                        label="Notes Section Title"
                        labelHidden
                        value={editableChart.notesTitle || 'Notes:'}
                        onChange={(value) => {
                          const updatedChart = JSON.parse(JSON.stringify(editableChart));
                          updatedChart.notesTitle = value;
                          setEditableChart(updatedChart);
                        }}
                      />
                      <Button
                        size="slim"
                        onClick={() => {
                          const updatedChart = JSON.parse(JSON.stringify(editableChart));
                          const chartDataRef = updatedChart.sizechart.sizechart || updatedChart.sizechart;
                          
                          if (!chartDataRef.notes) {
                            chartDataRef.notes = [];
                          }
                          
                          chartDataRef.notes.push('');
                          setEditableChart(updatedChart);
                        }}
                      >
                        Add Note
                      </Button>
                    </LegacyStack>
                    
                    {chartData?.notes && chartData.notes.length > 0 ? (
                      <div style={{ marginTop: '8px' }}>
                        {chartData.notes.map((note, index) => (
                          <LegacyStack key={index} alignment="center" distribution="fillEvenly" spacing="tight" wrap={false}>
                            <div style={{ flex: 1 }}>
                              <TextField
                                value={note || ''}
                                onChange={(value) => {
                                  const updatedChart = JSON.parse(JSON.stringify(editableChart));
                                  const chartDataRef = updatedChart.sizechart.sizechart || updatedChart.sizechart;
                                  chartDataRef.notes[index] = value;
                                  setEditableChart(updatedChart);
                                }}
                              />
                            </div>
                            <div>
                              <Button
                                size="slim"
                                destructive
                                onClick={() => {
                                  const updatedChart = JSON.parse(JSON.stringify(editableChart));
                                  const chartDataRef = updatedChart.sizechart.sizechart || updatedChart.sizechart;
                                  chartDataRef.notes.splice(index, 1);
                                  setEditableChart(updatedChart);
                                }}
                              >
                                Remove
                              </Button>
                            </div>
                          </LegacyStack>
                        ))}
                      </div>
                    ) : (
                      <div style={{ marginTop: '8px', textAlign: 'center' }}>
                        <Text variant="bodyMd" color="subdued" style={{ fontFamily: chartStyle.fontFamily }}>No notes yet. Click 'Add Note' to create one.</Text>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <Text variant="headingSm" style={{ color: chartStyle.fitGuideHeadingColor, fontFamily: chartStyle.fontFamily }}>{chartData.notesTitle || 'Notes:'}</Text>
                    <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                      {chartData.notes.map((note, index) => (
                        <li key={index} style={{ marginBottom: '4px' }}>
                          <Text variant="bodyMd" style={{ color: chartStyle.fitGuideTextColor, fontFamily: chartStyle.fontFamily }}>{note}</Text>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
            
            {/* Footer Text */}
            {footerText && (
              <div style={{ marginBottom: '16px', fontFamily: chartStyle.fontFamily }}>
                <Text variant="bodyMd" style={{ color: chartStyle.footerTextColor, fontFamily: chartStyle.fontFamily }}>{footerText}</Text>
              </div>
            )}
            
            {/* Footer Image */}
            {showFooterImage && footerImageUrl && (
              <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                <img 
                  src={footerImageUrl} 
                  alt="Footer" 
                  style={{ maxWidth: '100%', height: 'auto' }}
                />
              </div>
            )}
            
            {/* Info Text */}
            {infoText && (
              <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '4px', fontFamily: chartStyle.fontFamily }}>
                <Text variant="bodyMd" style={{ color: chartStyle.infoTextColor, fontFamily: chartStyle.fontFamily }}>{infoText}</Text>
              </div>
            )}
          </div>
          
          <LegacyStack distribution="equalSpacing">
            <div>
              <Button onClick={() => {
                if (isEditMode) {
                  // Ask for confirmation before discarding changes
                  if (window.confirm('Discard all changes?')) {
                    setIsEditMode(false);
                  }
                } else {
                  setIsChartPreviewModalOpen(false);
                }
              }}>
                {isEditMode ? 'Cancel' : 'Close'}
              </Button>
            </div>
            <div>
              {isEditMode ? (
                <>
                  <LegacyStack spacing="tight">
                    <Button onClick={handleRestoreChart} icon={DeleteMinor}>Restore Original</Button>
                    <Button onClick={handleSaveChart} primary>Save Changes</Button>
                  </LegacyStack>
                </>
              ) : (
                <Button onClick={handleEditChart} icon={EditMinor} primary>Edit Chart</Button>
              )}
            </div>
          </LegacyStack>
        </Modal.Section>
      </Modal>
    );
  };
  
  // If showing only template design or generated charts, don't show tabs
  return (
    <>
      {showTemplateDesignOnly ? (
        renderTemplateDesign()
      ) : showGeneratedChartsOnly ? (
        renderGeneratedCharts()
      ) : (
        <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange}>
          {selected === 0 && renderGeneratedCharts()}
          {selected === 1 && renderTemplateDesign()}
        </Tabs>
      )}
      
      {renderChartPreviewModal()}
      
      {/* Export Options Modal */}
      <SizeChartExportOptions
        open={isExportOptionsModalOpen}
        onClose={() => setIsExportOptionsModalOpen(false)}
        chartName={chartToExport?.name || chartToExport?.sizechart?.name || chartToExport?.sizechart?.sizechart?.name || 'sizechart'}
        onConfirm={(options, filename) => {
          if (exportOperation === 'export') {
            handleExportWithOptions(options, filename);
          } else if (exportOperation === 'upload') {
            handleUploadWithOptions(options, filename);
          }
        }}
        shopId={shopId}
        userId={userId}
        isLoading={isLoading}
        title={exportOperation === 'export' ? 'Export Options' : 'Upload to Shopify Options'}
        confirmText={exportOperation === 'export' ? 'Export' : 'Upload to Shopify'}
      />
      
      {error && (
        <Banner status="critical" onDismiss={() => setError(null)}>
          <p>{error}</p>
        </Banner>
      )}
    </>
  );
}